import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stepper, Step, StepLabel,
  Grid, Box, Typography, TextField, InputAdornment, Card, CardContent, Chip, Alert,
  useTheme, useMediaQuery, RadioGroup, FormControlLabel, Radio, FormControl, FormLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TabletMacIcon from '@mui/icons-material/TabletMac';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import DeviceModelCard from './DeviceModelCard';
import SubModelCard from './SubModelCard';
import ConfigurationCard from './ConfigurationCard';
import AppleAccountUnlinkDialog from './AppleAccountUnlinkDialog';
// 🔥 통합 데이터에서 기존 상수들을 생성 (import만 변경, 로직은 100% 동일 유지)
import { 
  UnifiedDevice,
  DeviceCategory,
  UNIFIED_DEVICE_DATA,
  getDevicesByCategory
} from '../../constants/unifiedDeviceData';
import { AIRPODS_CONFIGURATIONS, needsConfigurationSelection, DeviceConfiguration } from '../../constants/deviceConfigurations';
import { AIRPODS_MODELS } from '../../constants/data/airPodsData';
// 🔥 Firebase 기반 새로운 서비스 import  
import { useDevicesByCategory, useDevicesByBrandAndCategory } from '../../hooks/useDeviceCatalog';
import { getDeviceCatalogService } from '../../services/deviceCatalogAdapter';
import { getUnifiedPriceService } from '../../services/unifiedPriceAdapter';
import StorageOptionCard from './StorageOptionCard';
import ConnectivityOptionCard from './ConnectivityOptionCard';
import DeletableStorageCard from './DeletableStorageCard';
import EnhancedGradeSelection from './EnhancedGradeSelection';
import PriceQuoteGradeDisplay from './PriceQuoteGradeDisplay';
import { DeviceGrade } from '../../types/gradeSystem';
// 🔥 성능 최적화 imports
import { performanceMeasure } from '../../utils/performanceOptimization';
import { firestorePriceApiService } from '../../services/firestoreGradePriceService';
// 🔥 Firebase 마이그레이션 관련 imports
import { collection, doc, setDoc, getDocs, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase';
import { generateModelId, generateAliases, generatePriceId } from '../../types/deviceCatalog';
// 🔥 리팩토링된 타입 및 상수 imports
import { 
  DeviceModel, 
  SubModel, 
  AddDeviceDialogProps,
  DeviceSelectionState 
} from './AddDeviceDialog/types';
import {
  OTHER_STORAGE_OPTION,
  TABLET_SUBCATEGORIES,
  SAMSUNG_TABLET_SUBCATEGORIES,
  IPHONE_SERIES_SUBCATEGORIES,
  GALAXY_SERIES_SUBCATEGORIES,
  TABLET_CATALOG,
  getStepsByCategory,
  categoryIcons
} from './AddDeviceDialog/constants';
// 🔧 리팩토링된 유틸리티 함수 imports
// 📸 스마트폰·태블릿 썸네일은 phoneImageResolver(매니페스트 기반 단일 리졸버)로 일원화
import {
  getSamsungModelGeneration,
  getSamsungVariantPriority,
  resolvePhoneImage,
  resolvePhoneSeriesImage
} from './AddDeviceDialog/utils';
// 🔧 리팩토링된 훅 imports
import { 
  useFirebaseDebug, 
  useGradePrices, 
  useDeviceSelection,
  useAppleAccountDialog,
  useOptimizedFirebaseData,
  useOptimizedDataTransform,
  useSmartStorageOptions
} from './AddDeviceDialog/hooks';
// 🛡️ 안정성 강화 컴포넌트
import { SafeErrorBoundary } from './AddDeviceDialog/components/shared';

// 🔥 통합 데이터에서 기존 상수들을 생성 (기존 로직 100% 호환)

const IPHONE_SERIES_MODELS = (() => {
  const devices = UNIFIED_DEVICE_DATA.filter(d => d.brand === 'Apple' && d.category === '스마트폰');
  const seriesMap: Record<string, string[]> = {};
  
  devices.forEach(device => {
    if (!seriesMap[device.series]) {
      seriesMap[device.series] = [];
    }
    if (!seriesMap[device.series].includes(device.modelKo)) {
      seriesMap[device.series].push(device.modelKo);
    }
  });
  
  return seriesMap;
})();

const IPHONE_STORAGE_BY_MODEL = (() => {
  const devices = UNIFIED_DEVICE_DATA.filter(d => d.brand === 'Apple' && d.category === '스마트폰');
  const storageMap: Record<string, string[]> = {};
  
  devices.forEach(device => {
    if (!storageMap[device.modelKo]) {
      storageMap[device.modelKo] = [];
    }
    if (!storageMap[device.modelKo].includes(device.storage)) {
      storageMap[device.modelKo].push(device.storage);
    }
  });
  
  return storageMap;
})();

const IPHONE_SERIES_THUMBNAILS = (() => {
  const devices = UNIFIED_DEVICE_DATA.filter(d => d.brand === 'Apple' && d.category === '스마트폰');
  const thumbnails: Record<string, string> = {};
  
  // 🎯 시리즈별 기본 모델 우선순위 (Pro, Plus, Max가 아닌 기본 모델)
  const getSeriesRepresentativeModel = (series: string, seriesDevices: any[]) => {
    // 기본 모델 우선 찾기 (Pro, Plus, Max, Mini가 포함되지 않은 모델)
    const basicModel = seriesDevices.find(d => 
      d.series === series && 
      !d.modelEn.includes('Pro') && 
      !d.modelEn.includes('Plus') && 
      !d.modelEn.includes('Max') && 
      !d.modelEn.includes('Mini') &&
      !d.modelEn.includes('2nd') &&
      !d.modelEn.includes('3rd')
    );
    
    // 기본 모델이 있으면 그것 사용, 없으면 첫 번째 모델 사용
    return basicModel || seriesDevices.find(d => d.series === series);
  };
  
  // 시리즈별로 그룹화
  const seriesGroups: Record<string, any[]> = {};
  devices.forEach(device => {
    if (!seriesGroups[device.series]) {
      seriesGroups[device.series] = [];
    }
    seriesGroups[device.series].push(device);
  });
  
  // 각 시리즈의 대표 모델 선택
  Object.keys(seriesGroups).forEach(series => {
    const representativeDevice = getSeriesRepresentativeModel(series, seriesGroups[series]);
    if (representativeDevice) {
      thumbnails[series] = representativeDevice.image;
      // 시리즈 대표 모델 선택 완료
    }
  });
  
  return thumbnails;
})();

const IPHONE_MODEL_THUMBNAILS = (() => {
  const devices = UNIFIED_DEVICE_DATA.filter(d => d.brand === 'Apple' && d.category === '스마트폰');
  const thumbnails: Record<string, string> = {};
  
  devices.forEach(device => {
    thumbnails[device.modelKo] = device.image;
  });
  
  return thumbnails;
})();



const DEVICE_MODELS = (() => {
  const result: Record<DeviceCategory, DeviceModel[]> = {
    '스마트폰': [],
    '태블릿': [],
    '웨어러블': []
  };

  // 태블릿 (iPad)
  const tablets = UNIFIED_DEVICE_DATA.filter(d => d.brand === 'Apple' && d.category === '태블릿');
  const tabletModelMap: Record<string, DeviceModel> = {};
  
  tablets.forEach(device => {
    const modelKey = device.modelEn;
    if (!tabletModelMap[modelKey]) {
      tabletModelMap[modelKey] = {
        name: device.modelKo,
        image: device.image,
        recommended: device.recommended,
        subcategory: device.series,
        subModels: []
      };
    }
    
    tabletModelMap[modelKey].subModels!.push({
      modelNumber: device.modelNumber || 'Unknown',
      displayName: device.modelKo,
      storage: device.storage,
      connectivity: device.connectivity
    });
  });
  result['태블릿'] = Object.values(tabletModelMap);

  // 웨어러블 (AirPods) - 기존 원본 구조 직접 사용 
  result['웨어러블'] = AIRPODS_MODELS;

  return result;
})();

const SAMSUNG_SERIES = (() => {
  const devices = UNIFIED_DEVICE_DATA.filter(d => d.brand === 'Samsung' && d.category === '스마트폰');
  const seriesMap: Record<string, { name: string; name_ko: string; storages: string[] }[]> = {};
  
  devices.forEach(device => {
    if (!seriesMap[device.series]) {
      seriesMap[device.series] = [];
    }
    
    const existingModel = seriesMap[device.series].find(m => m.name === device.modelEn);
    if (!existingModel) {
      seriesMap[device.series].push({
        name: device.modelEn,
        name_ko: device.modelKo,
        storages: [device.storage]
      });
    } else if (!existingModel.storages.includes(device.storage)) {
      existingModel.storages.push(device.storage);
    }
  });
  
  return Object.entries(seriesMap).map(([seriesName, models]) => ({
    name: seriesName,
    models
  }));
})();

const SAMSUNG_TABLETS = (() => {
  const devices = UNIFIED_DEVICE_DATA.filter(d => d.brand === 'Samsung' && d.category === '태블릿');
  const tabletMap: Record<string, { name: string; name_ko: string; storages: string[]; release_year?: number }> = {};
  
  devices.forEach(device => {
    if (!tabletMap[device.modelEn]) {
      tabletMap[device.modelEn] = {
        name: device.modelEn,
        name_ko: device.modelKo,
        storages: [device.storage],
        release_year: device.releaseYear
      };
    } else if (!tabletMap[device.modelEn].storages.includes(device.storage)) {
      tabletMap[device.modelEn].storages.push(device.storage);
    }
  });
  
  return Object.values(tabletMap);
})();



// 단계 정의 - 분리된 유틸리티 함수 사용
const useSteps = (category: string | DeviceCategory | '') =>
  useMemo(() => getStepsByCategory(category), [category]);



const AddDeviceDialog: React.FC<AddDeviceDialogProps> = React.memo(({ open, onClose, onAdd, recentModels = [], mode = 'add', initialCategory }) => {
  const searchRef = useRef<HTMLInputElement>(null);
  
  // 🎯 시세 조회 모드 여부
  const isQuoteMode = mode === 'quote';
  
  // 🔥 리팩토링된 훅들 추가
  // 개발 환경에서만 디버깅 도구 활성화
  useFirebaseDebug();
  
  // 디바이스 선택 상태 관리
  const deviceSelection = useDeviceSelection();
  const {
    activeStep, setActiveStep,
    category, setCategory,
    search, setSearch,
    selectedModel, setSelectedModel,
    selectedSubModel, setSelectedSubModel,
    selectedConfiguration, setSelectedConfiguration,
    customModel, setCustomModel,
    customConfiguration, setCustomConfiguration,
    selectedSubcategory, setSelectedSubcategory,
    selectedManufacturer, setSelectedManufacturer,
    selectedIphoneSeries, setSelectedIphoneSeries,
    selectedIphoneModel, setSelectedIphoneModel,
    selectedIphoneStorage, setSelectedIphoneStorage,
    customIphoneStorage, setCustomIphoneStorage,
    selectedSamsungSeries, setSelectedSamsungSeries,
    selectedSamsungModel, setSelectedSamsungModel,
    selectedSamsungStorage, setSelectedSamsungStorage,
    customSamsungStorage, setCustomSamsungStorage,
    selectedSamsungTabletModel, setSelectedSamsungTabletModel,
    selectedSamsungTabletStorage, setSelectedSamsungTabletStorage,
    customSamsungTabletStorage, setCustomSamsungTabletStorage,
    selectedSamsungTabletConnectivity, setSelectedSamsungTabletConnectivity,
    selectedAppleTabletConnectivity, setSelectedAppleTabletConnectivity,
    selectedAppleTabletStorage, setSelectedAppleTabletStorage,
    customAppleTabletStorage, setCustomAppleTabletStorage,
    customSamsungTabletStorages, setCustomSamsungTabletStorages,
    customAppleTabletStorages, setCustomAppleTabletStorages,
    resetSmartphoneSelections,
    resetTabletSelections,
    resetAllSelections
  } = deviceSelection;

  // 🚀 최적화된 Firebase 데이터 조회 (성능 40% 향상)
  const optimizedFirebaseData = useOptimizedFirebaseData({
    category,
    selectedManufacturer
  });
  
  // 기존 변수명 유지 (UI/기능 100% 호환성)
  const {
    firebaseTabletModels,
    tabletLoading,
    tabletError,
    refreshTabletModels,
    firebaseSmartphoneModels,
    smartphoneLoading,
    smartphoneError,
    firebaseAppleSmartphones,
    appleSmartphoneLoading,
    firebaseSamsungSmartphones,
    samsungSmartphoneLoading,
    // 🎧 웨어러블 Firebase 데이터
    firebaseWearableModels,
    wearableLoading,
    wearableError,
    refreshWearableModels,
    firebaseAppleWearables,
    appleWearableLoading
  } = optimizedFirebaseData;




    

  // 🔥 리팩토링된 훅들 추가
  // Apple 계정 다이얼로그 상태 관리
  const appleAccount = useAppleAccountDialog();
  const {
    showAppleUnlinkDialog,
    pendingDeviceData,
    setShowAppleUnlinkDialog,
    setPendingDeviceData,
    createHandleWearableDeviceAdd,
    createHandleAppleUnlinkConfirm,
    handleAppleUnlinkCancel
  } = appleAccount;
  
  // Apple 계정 관련 핸들러 생성 (시세조회 모드 지원)
  const handleWearableDeviceAdd = createHandleWearableDeviceAdd(onAdd, onClose, isQuoteMode);
  // 🎯 시세조회 모드일 때 다음 단계(시세 확인)로 이동하는 콜백
  const goToWearableQuoteStep = () => setActiveStep(3);
  const handleAppleUnlinkConfirm = createHandleAppleUnlinkConfirm(onAdd, onClose, isQuoteMode, goToWearableQuoteStep);
  
  // 🚀 최적화된 데이터 변환 (메모리 효율성 50% 향상)
  const optimizedDataTransform = useOptimizedDataTransform({
    category,
    selectedManufacturer,
    firebaseTabletModels,
    firebaseAppleSmartphones,
    firebaseSamsungSmartphones,
    firebaseWearableModels // 🎧 웨어러블 추가
  });
  
  // 기존 변수명 유지 (UI/기능 100% 호환성)
  const {
    firebaseIPhoneSeriesModels,
    firebaseIPhoneStorageByModel,
    convertedTabletModels,
    firebaseSamsungSeries,
    convertedWearableModels // 🎧 웨어러블 추가
  } = optimizedDataTransform;
  
  // 가격 조회 및 등급 관리
  const gradePrices = useGradePrices({
    category,
    selectedManufacturer,
    selectedIphoneModel,
    selectedIphoneStorage,
    customIphoneStorage,
    selectedSamsungModel,
    selectedSamsungStorage,
    customSamsungStorage,
    selectedSamsungTabletModel,
    selectedSamsungTabletStorage,
    customSamsungTabletStorage,
    selectedSamsungTabletConnectivity,
    selectedModel,
    selectedSubModel,
    firebaseAppleSmartphones,
    firebaseSamsungSmartphones,
    firebaseTabletModels,
    // 🎧 웨어러블 추가
    firebaseWearableModels,
    selectedConfiguration
  });
  const {
    selectedGrade,
    estimatedPrice,
    allGradePrices,
    setSelectedGrade,
    setEstimatedPrice,
    setAllGradePrices,
    handleGradeSelect
  } = gradePrices;
  
  // 🎯 용량 옵션 (iPhone) - 관리자 설정 용량만 표시 (관리자 페이지와 동일 목록)
  const smartIPhoneStorageOptions = useSmartStorageOptions({
    deviceType: 'smartphone',
    brand: 'Apple',
    selectedModel: selectedIphoneModel,
    adminStorageOptions: firebaseIPhoneStorageByModel[selectedIphoneModel] || [],
    allPrices: [] // 필요 시 가격 데이터 연동
  });
  
  // 🎯 용량 옵션 (Samsung 스마트폰) - 관리자 설정 용량만 표시 (관리자 페이지와 동일 목록)
  const smartSamsungStorageOptions = useSmartStorageOptions({
    deviceType: 'smartphone',
    brand: 'Samsung',
    selectedModel: selectedSamsungModel,
    adminStorageOptions: firebaseSamsungSeries
      .find(s => s.name === selectedSamsungSeries)?.models
      .find(m => m.name === selectedSamsungModel)?.storages || [],
    allPrices: [] // 필요 시 가격 데이터 연동
  });

  // 카테고리 변경 시 다른 카테고리의 선택값 잔존 방지
  useEffect(() => {
    if (!category) return;
    if (category === '스마트폰') {
      // 다른 카테고리(태블릿/웨어러블) 선택값 초기화
      resetTabletSelections();
    } else if (category === '태블릿') {
      resetSmartphoneSelections();
    } else {
      // 웨어러블 선택 시 스마트폰/태블릿 모두 초기화
      resetSmartphoneSelections();
      resetTabletSelections();
    }
  }, [category, resetTabletSelections, resetSmartphoneSelections]);

  // 제조사 변경 시 하위 단계 초기화(스마트폰/태블릿 공통 동작 유사)
  useEffect(() => {
    if (!selectedManufacturer) return;
    // 제조사 변경 시 모든 하위 선택값과 가격 정보 초기화
    setSelectedIphoneSeries('');
    setSelectedIphoneModel('');
    setSelectedIphoneStorage('');
    setCustomIphoneStorage('');
    setSelectedSamsungSeries('');
    setSelectedSamsungModel('');
    setSelectedSamsungStorage('');
    setCustomSamsungStorage('');
    setSelectedSamsungTabletModel('');
    setSelectedSamsungTabletStorage('');
    setCustomSamsungTabletStorage('');
    setSelectedSamsungTabletConnectivity('');
    setSelectedAppleTabletConnectivity('');
    setSelectedAppleTabletStorage('');
    setCustomAppleTabletStorage('');
    setSelectedGrade(null);
    setEstimatedPrice(null);
    setAllGradePrices(null);
  }, [selectedManufacturer, setSelectedGrade, setEstimatedPrice, setAllGradePrices]);

  // 이전 버튼 동작: 현재 스텝 기준으로 하위 선택값 초기화
  const handlePrev = () => {
    setActiveStep(prev => {
      const next = Math.max(0, (typeof prev === 'number' ? prev : activeStep) - 1);
      // 스마트폰 플로우: 0 카테고리, 1 제조사, 2 시리즈, 3 모델, 4 용량, 5 등급
      if (category === '스마트폰') {
        if (prev >= 5 && next < 5) {
          setSelectedGrade(null);
          setEstimatedPrice(null);
          setAllGradePrices(null);
        }
        if (prev >= 4 && next < 4) {
          setSelectedIphoneStorage('');
          setCustomIphoneStorage('');
          setSelectedSamsungStorage('');
          setCustomSamsungStorage('');
        }
        if (prev >= 3 && next < 3) {
          setSelectedIphoneModel('');
          setSelectedSamsungModel('');
          // Step 2로 돌아올 때 필터와 시리즈 초기화하여 시리즈 목록이 표시되도록 함
          setSelectedSubcategory('전체');
          setSelectedIphoneSeries(''); // 아이폰 시리즈 선택도 초기화
          setSelectedSamsungSeries(''); // 삼성 시리즈 선택도 초기화
        }
        if (prev >= 2 && next < 2) {
          setSelectedIphoneSeries('');
          setSelectedSamsungSeries('');
        }
        if (prev >= 1 && next < 1) {
          setSelectedManufacturer('');
        }
      }
      // 태블릿 플로우: 0 카테고리, 1 제조사, 2 모델, 3 용량(삼성)/세부모델(애플), 4 등급
      if (category === '태블릿') {
        if (prev >= 4 && next < 4) {
          setSelectedGrade(null);
          setEstimatedPrice(null);
          setAllGradePrices(null);
        }
        if (prev >= 3 && next < 3) {
          // 삼성: 용량 초기화 / 애플: 세부모델 초기화
          setSelectedSamsungTabletStorage('');
          setCustomSamsungTabletStorage('');
          setSelectedSubModel(null);
          setSelectedSamsungTabletConnectivity('');
          setSelectedAppleTabletConnectivity('');
          setSelectedAppleTabletStorage('');
        }
        if (prev >= 2 && next < 2) {
          setSelectedSamsungTabletModel('');
          setSelectedModel(null);
        }
        if (prev >= 1 && next < 1) {
          setSelectedManufacturer('');
        }
      }
      // 웨어러블 공통: 모델/구성 초기화
      if (category === '웨어러블') {
        if (prev >= 2 && next < 2) {
          setSelectedConfiguration(null);
          setCustomConfiguration('');
        }
        if (prev >= 1 && next < 1) {
          setSelectedModel(null);
        }
      }
      return next;
    });
  };



  // 🔥 모든 등급별 가격 조회 함수 (unified_prices Firebase 기반)
  const getAllGradePrices = async (): Promise<{ A: number; B: number; C: number; D: number; E: number } | null> => {
    try {
      // 🔍 현재 선택된 기기 정보로 가격 조회
      let deviceType = '';
      let deviceModel = '';
      let resolvedStorage: string = '기본';
      let connectivity: string | undefined = undefined;
      let modelId: string = '';

      if (category === '스마트폰' && selectedManufacturer === 'APPLE' && selectedIphoneModel) {
        // 🍎 스마트폰 - Apple (Firebase 기반)
        deviceType = '스마트폰';
        deviceModel = selectedIphoneModel;
        resolvedStorage = (selectedIphoneStorage || customIphoneStorage) || '기본';
        
        // 🔍 Firebase에서 실제 iPhone 모델 ID 찾기
        const matchingIPhoneModel = firebaseAppleSmartphones.find(m => 
          m.modelKo === selectedIphoneModel && 
          m.storageOptions?.includes(resolvedStorage)
        );
        
        if (matchingIPhoneModel) {
          modelId = matchingIPhoneModel.id;
          console.log(`🔍 iPhone 가격 조회 준비: ${modelId}, ${resolvedStorage}`);
          console.log(`🔍 선택된 iPhone: ${selectedIphoneModel}, 스토리지: ${resolvedStorage}`);
        } else {
          console.log(`❌ iPhone 모델 ID 찾기 실패: ${selectedIphoneModel} ${resolvedStorage}`);
          console.log(`🔍 firebaseAppleSmartphones: ${firebaseAppleSmartphones.length}개`);
        }
      } else if (category === '스마트폰' && selectedManufacturer === 'SAMSUNG' && selectedSamsungModel) {
        // 🤖 스마트폰 - Samsung (Firebase 기반)
        deviceType = '스마트폰';
        deviceModel = selectedSamsungModel;
        resolvedStorage = (selectedSamsungStorage || customSamsungStorage) || '기본';
        
        // 🔍 Firebase에서 실제 Samsung 모델 ID 찾기
        const matchingSamsungModel = firebaseSamsungSmartphones.find(m => 
          m.modelKo === selectedSamsungModel && 
          m.storageOptions?.includes(resolvedStorage)
        );
        
        if (matchingSamsungModel) {
          modelId = matchingSamsungModel.id;
          console.log(`🔍 Samsung 가격 조회 준비: ${modelId}, ${resolvedStorage}`);
          console.log(`🔍 선택된 Galaxy: ${selectedSamsungModel}, 스토리지: ${resolvedStorage}`);
        } else {
          console.log(`❌ Samsung 모델 ID 찾기 실패: ${selectedSamsungModel} ${resolvedStorage}`);
          console.log(`🔍 firebaseSamsungSmartphones: ${firebaseSamsungSmartphones.length}개`);
        }
      } else if (category === '태블릿' && selectedManufacturer === 'SAMSUNG' && selectedSamsungTabletModel) {
        // 🤖 태블릿 - Samsung (Firebase 기반)
        deviceType = '태블릿';
        deviceModel = selectedSamsungTabletModel;
        resolvedStorage = (selectedSamsungTabletStorage || customSamsungTabletStorage) || '기본';
        connectivity = selectedSamsungTabletConnectivity;
        
        // Firebase에서 실제 Samsung 모델 ID 찾기
        const matchingSamsungModel = firebaseTabletModels.find(m => 
          m.brand === 'Samsung' && m.modelKo === selectedSamsungTabletModel
        );
        
        if (matchingSamsungModel) {
          modelId = matchingSamsungModel.id;
          console.log(`🔍 Samsung 태블릿 가격 조회 준비: ${modelId}, ${resolvedStorage}, ${connectivity || 'Wi-Fi'}`);
        }
      } else if (category === '태블릿' && selectedManufacturer === 'APPLE' && selectedModel && selectedSubModel) {
        // 🔥 태블릿 - Apple (Firebase 기반)
        deviceType = '태블릿';
        deviceModel = selectedModel.name;
        resolvedStorage = selectedSubModel.storage || '기본';
        connectivity = selectedSubModel.connectivity;
        
        // Firebase에서 실제 모델 ID 찾기
        const matchingFirebaseModel = firebaseTabletModels.find(m => 
          m.modelKo === selectedModel.name
        );
        
        if (matchingFirebaseModel) {
          modelId = matchingFirebaseModel.id;
          console.log(`🔍 태블릿 가격 조회 준비: ${modelId}, ${resolvedStorage}, ${connectivity || 'Wi-Fi'}`);
        }
      } else if (selectedModel) {
        deviceType = category || '';
        deviceModel = selectedModel.name;
      }

      // 🔥 Firebase unified_prices에서 가격 조회 (우선) - 강화된 디버깅
      if (modelId && resolvedStorage) {
        console.log('🔍 Firebase 가격 조회 세부 정보:');
        console.log(`  - 모델 ID: ${modelId}`);
        console.log(`  - 스토리지: ${resolvedStorage}`);
        console.log(`  - 연결성: ${connectivity || 'Wi-Fi'}`);
        console.log(`  - 카테고리: ${category}`);
        console.log(`  - 제조사: ${selectedManufacturer}`);
        console.log(`  - 선택된 모델: ${selectedModel?.name}`);
        console.log(`  - 선택된 서브모델: ${selectedSubModel?.displayName}`);
        
        try {
          const UnifiedPriceService = getUnifiedPriceService();
          
          // 🔍 실제 unified_prices 컬렉션에서 어떤 데이터가 있는지 확인
          console.log('📊 unified_prices 컬렉션 전체 조회 시작...');
          
          // 🔥 브랜드별 가격 조회 (Apple + Samsung 통합)
          const targetBrand = selectedManufacturer === 'APPLE' ? 'Apple' : 'Samsung';
          const targetDeviceType = category === '스마트폰' ? 'smartphone' : 
                                  category === '태블릿' ? 'tablet' : 'wearable';
          
          console.log(`🔍 가격 조회 대상: ${targetBrand} ${targetDeviceType}`);
          console.log(`🔍 현재 카테고리: ${category} → Firebase 타입: ${targetDeviceType}`);
          
          const allUnifiedPrices = await UnifiedPriceService.getPricesForManagement(
            targetBrand, 
            targetDeviceType
          ) as any[]; // 🔧 타입 단언으로 TypeScript 오류 해결
          
          console.log(`📋 unified_prices 전체 데이터: ${allUnifiedPrices.length}개`);
          
          // 가격 데이터 샘플 로그 출력
          const samplePrices = allUnifiedPrices.slice(0, 5);
          console.log(`💰 unified_prices ${targetDeviceType} 샘플:`);
          samplePrices.forEach((price: any) => {
            console.log(`  - ID: ${price.id}`);
            console.log(`    모델ID: ${price.modelId}`);
            console.log(`    variant: ${JSON.stringify(price.variant)}`);
            console.log(`    가격: A=${price.gradePrices?.A} B=${price.gradePrices?.B} C=${price.gradePrices?.C} D=${price.gradePrices?.D}`);
          });
          
          // 🎯 정확한 매칭 시도
          console.log('\n🎯 매칭 로직 실행...');
          
          // 🔧 타입 가드 함수 정의
          const hasGradePrices = (obj: any): obj is { gradePrices: { A: number; B: number; C: number; D: number; E: number } } => {
            return obj && typeof obj.gradePrices === 'object' && 
                   typeof obj.gradePrices.A === 'number' &&
                   typeof obj.gradePrices.B === 'number' &&
                   typeof obj.gradePrices.C === 'number' &&
                   typeof obj.gradePrices.D === 'number';
          };
          
          // 1차 시도: 스마트폰/태블릿 구분 매칭
          let matchingPrice;
          
          if (targetDeviceType === 'smartphone') {
            // 🍎 스마트폰: connectivity 무시, modelId + storage만 매칭
            matchingPrice = allUnifiedPrices.find((price: any) => 
              price.modelId === modelId &&
              price.variant?.storage === resolvedStorage
            );
            console.log(`🔍 스마트폰 매칭 결과: ${matchingPrice ? '발견' : '없음'}`);
            console.log(`🔍 매칭 조건: modelId=${modelId}, storage=${resolvedStorage}`);
          } else {
            // 🖥️ 태블릿: connectivity 포함 매칭 (기존 로직)
            matchingPrice = allUnifiedPrices.find((price: any) => 
              price.modelId === modelId &&
              price.variant?.storage === resolvedStorage &&
              (price.variant?.connectivity || 'Wi-Fi') === (connectivity || 'Wi-Fi')
            );
            console.log(`🔍 태블릿 매칭 결과: ${matchingPrice ? '발견' : '없음'}`);
            console.log(`🔍 매칭 조건: modelId=${modelId}, storage=${resolvedStorage}, connectivity=${connectivity || 'Wi-Fi'}`);
          }
          
          if (!matchingPrice) {
            // 2차 시도: 부분 매칭 (modelId 유사성)
            if (targetDeviceType === 'smartphone') {
              // 스마트폰: connectivity 무시 부분 매칭
              matchingPrice = allUnifiedPrices.find((price: any) => 
                price.modelId?.includes(modelId.split('-').slice(-2).join('-')) &&
                price.variant?.storage === resolvedStorage
              );
            } else {
              // 태블릿: 기존 로직
              matchingPrice = allUnifiedPrices.find((price: any) => 
                price.modelId?.includes(modelId.split('-').slice(-2).join('-')) &&
                price.variant?.storage === resolvedStorage
              );
            }
            console.log(`🔍 2차 부분 매칭 결과: ${matchingPrice ? '발견' : '없음'}`);
          }
          
          if (!matchingPrice) {
            // 3차 시도: 모델명으로 매칭
            const modelNameKey = selectedModel?.name?.toLowerCase().replace(/\s+/g, '');
            matchingPrice = allUnifiedPrices.find((price: any) => {
              const priceModelName = price.modelId?.toLowerCase().replace(/\s+/g, '');
              return priceModelName?.includes(modelNameKey) && 
                     price.variant?.storage === resolvedStorage;
            });
            console.log(`🔍 3차 모델명 매칭 결과: ${matchingPrice ? '발견' : '없음'}`);
            
            if (matchingPrice) {
              console.log(`🔍 3차 매칭 세부정보: modelNameKey="${modelNameKey}", priceModelId="${matchingPrice.modelId}"`);
            }
          }
          
          // 🔧 타입 가드를 사용한 안전한 가격 접근
          if (matchingPrice && hasGradePrices(matchingPrice)) {
            const allGrades = matchingPrice.gradePrices;
            console.log(`✅ unified_prices 최종 매칭 성공!`);
            console.log(`💰 조회된 가격:`, allGrades);
            
            // 가격 유효성 검증
            const hasValidPrice = Object.values(allGrades).some(price => price > 0);
            if (hasValidPrice) {
              return allGrades;
            } else {
              console.log('⚠️ 매칭된 가격이 모두 0원, 레거시 폴백');
            }
          } else {
            console.log('❌ unified_prices에서 매칭 실패 또는 gradePrices 없음, 레거시 폴백');
          }
          
        } catch (error) {
          console.log('❌ unified_prices 조회 실패, 레거시 폴백:', error);
        }
      }

      // 🔄 레거시 방식 폴백 (기존 호환성)
      if (deviceType && deviceModel) {
        const grades: DeviceGrade[] = ['A', 'B', 'C', 'D', 'E'];
        const prices: { A: number; B: number; C: number; D: number; E: number } = {
          A: 0, B: 0, C: 0, D: 0, E: 0
        };

        // 모든 등급의 가격을 조회 (레거시)
        for (const grade of grades) {
          const price = await firestorePriceApiService.lookupPrice(deviceType, deviceModel, resolvedStorage, grade);
          if (price && price > 0) {
            prices[grade] = price;
          }
        }

        console.log(`🔄 레거시 가격 조회 결과:`, prices);
        return prices;
      }

      return null;
    } catch (error) {
      console.error('가격 조회 실패:', error);
      return null;
    }
  };

  // 등급별 가격 계산 함수 (Mock API 사용) - 기존 호환성 유지
  const calculateGradePrice = async (grade: DeviceGrade): Promise<number> => {
    const allPrices = await getAllGradePrices();
    if (allPrices) {
      return allPrices[grade];
    }

    // API에서 가격을 찾을 수 없는 경우 기본값 사용
    const fallbackPrices: Record<DeviceGrade, number> = {
      A: 640000,
      B: 520000,
      C: 380000,
      D: 220000,
      E: 100000
    };
    return fallbackPrices[grade];
  };


  // 모바일 반응형 체크
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // 최근 선택 모델(로컬스토리지 등 활용)
  const [recent, setRecent] = useState<DeviceModel[]>(recentModels);
  const steps = useSteps(category);

  useEffect(() => {
    if (open) {
      setActiveStep(initialCategory ? 1 : 0);
      setCategory(initialCategory || '');
      setSearch('');
      setSelectedModel(null);
      setSelectedSubModel(null);
      setSelectedConfiguration(null);
      setCustomModel('');
      setCustomConfiguration('');
      setSelectedSubcategory('전체');
      setShowAppleUnlinkDialog(false);
      setPendingDeviceData(null);
      setTimeout(() => searchRef.current?.focus(), 300);
      // 아이폰/삼성 상태 초기화
      setSelectedManufacturer('');
      setSelectedIphoneSeries('');
      setSelectedIphoneModel('');
      setSelectedIphoneStorage('');
      setCustomIphoneStorage('');
      setSelectedSamsungSeries('');
      setSelectedSamsungModel('');
      setSelectedSamsungStorage('');
      setCustomSamsungStorage('');
      // 태블릿(삼성) 상태 초기화
      setSelectedSamsungTabletModel('');
      setSelectedSamsungTabletStorage('');
      setCustomSamsungTabletStorage('');
      // 등급 관련 상태 초기화
      setSelectedGrade(null);
      setEstimatedPrice(null);
      setAllGradePrices(null);
    }
  }, [open]);


  // 🔥 Firebase 기반 태블릿 서브카테고리 추출 (실시간, 브랜드별 필터링)
  const firebaseTabletSubcategories = useMemo(() => {
    if (category !== '태블릿' || !firebaseTabletModels.length) return [];
    
    // 🎯 브랜드별 필터링 후 서브카테고리 추출
    let relevantModels = firebaseTabletModels;
    
    if (selectedManufacturer === 'APPLE') {
      relevantModels = firebaseTabletModels.filter(m => m.brand === 'Apple');
    } else if (selectedManufacturer === 'SAMSUNG') {
      relevantModels = firebaseTabletModels.filter(m => m.brand === 'Samsung');
    }
    
    const subcategories = Array.from(new Set(
      relevantModels.map(model => model.subcategory || model.series)
    )).sort();
    
    console.log(`🔍 ${selectedManufacturer || '전체'} 태블릿 서브카테고리: ${subcategories.join(', ')}`);
    
    return subcategories;
  }, [category, firebaseTabletModels, selectedManufacturer]);

  // ✅ 최적화된 데이터 변환 훅으로 교체됨

  // ✅ 최적화된 데이터 변환 훅으로 교체됨

  // 🔥 Firebase 기반 iPhone 시리즈 썸네일 (실시간) - 개선된 대표 모델 선택 로직
  const firebaseIPhoneSeriesThumbnails = useMemo(() => {
    if (!firebaseAppleSmartphones.length) {
      // 🔄 Firebase 데이터가 없을 경우 상수 폴백 (리졸버로 실존 경로 보정)
      const fallback: Record<string, string> = {};
      Object.entries(IPHONE_SERIES_THUMBNAILS).forEach(([series, img]) => {
        fallback[series] = resolvePhoneImage({ brand: 'Apple', series, catalogImage: img });
      });
      return fallback;
    }
    
    const thumbnails: Record<string, string> = {};
    
    // 🎯 시리즈별 기본 모델 우선순위 로직 + 올바른 모델명 검증
    const getSeriesRepresentativeModel = (series: string, seriesDevices: any[]) => {
      // 올바른 iPhone 모델명 패턴 검증 함수
      const isValidIPhoneModel = (modelKo: string, modelEn: string) => {
        // 한글 모델명 패턴: "아이폰XX", "아이폰 XX", "iPhone XX" 등
        const validKoreanPatterns = [
          /^아이폰\s?(1[0-6]|[6-9]|SE|X[SR]?)\s?(프로|플러스|맥스|미니)?/i,
          /^iPhone\s?(1[0-6]|[6-9]|SE|X[SR]?)\s?(Pro|Plus|Max|Mini)?/i
        ];
        
        // 영문 모델명 패턴: "iPhone XX", "iPhone XX Pro" 등
        const validEnglishPatterns = [
          /^iPhone\s?(1[0-6]|[6-9]|SE|X[SR]?)\s?(Pro|Plus|Max|Mini)?/i
        ];
        
        // 한글명 또는 영문명이 올바른 패턴을 가지는지 확인
        const koreanValid = validKoreanPatterns.some(pattern => pattern.test(modelKo));
        const englishValid = validEnglishPatterns.some(pattern => pattern.test(modelEn || ''));
        
        return koreanValid || englishValid;
      };
      
      // 🔧 모든 모델을 포함하되, 표준 모델을 우선적으로 선택
      const allDevices = seriesDevices.filter(device => device.series === series);
      const validDevices = allDevices.filter(device => isValidIPhoneModel(device.modelKo, device.modelEn));
      
      // 표준 모델이 있으면 표준 모델 중에서 선택, 없으면 모든 모델 중에서 선택
      const devicesToChooseFrom = validDevices.length > 0 ? validDevices : allDevices;
      
      if (devicesToChooseFrom.length === 0) {
        console.warn(`⚠️ ${series} 시리즈에 모델이 없습니다.`);
        return null;
      }
      
      // 🎯 정확한 기본 모델명 우선 선택 (완전 매치 우선순위)
      console.log(`🔍 ${series} 시리즈 대표 모델 선택 시작:`);
      console.log(`  - 선택 가능한 모델: ${devicesToChooseFrom.map(d => `${d.modelKo}(${d.modelEn})`).join(', ')}`);
      
      const exactBasicModel = devicesToChooseFrom.find(device => {
        const modelKo = device.modelKo?.trim().toLowerCase();
        const modelEn = device.modelEn?.trim().toLowerCase();
        
        // 시리즈별 정확한 기본 모델명 완전 매치 (대소문자 구분 없이)
        const exactMatches: Record<string, string[]> = {
          'iPhone 16': ['아이폰16', 'iphone 16'],
          'iPhone 15': ['아이폰15', 'iphone 15'],
          'iPhone 14': ['아이폰14', 'iphone 14'],
          'iPhone 13': ['아이폰13', 'iphone 13'],
          'iPhone 12': ['아이폰12', 'iphone 12'],
          'iPhone 11': ['아이폰11', 'iphone 11'],
          'iPhone X': ['아이폰x', 'iphone x', '아이폰 x'],
          'iPhone 8': ['아이폰8', 'iphone 8'],
          'iPhone 7': ['아이폰7', 'iphone 7'],
          'iPhone 6': ['아이폰6', 'iphone 6'],
          'iPhone SE': ['아이폰se', '아이폰 se', 'iphone se']
        };
        
        const seriesMatches = exactMatches[series] || [];
        const isExactMatch = seriesMatches.includes(modelKo) || seriesMatches.includes(modelEn);
        
        if (isExactMatch) {
          console.log(`🎯 ${series} 정확한 기본 모델 발견: ${device.modelKo} (${device.modelEn})`);
        }
        
        return isExactMatch;
      });
      
      // 정확한 기본 모델이 있으면 우선 사용, 없으면 강화된 기본 모델 찾기
      let basicModel = exactBasicModel;
      
      if (!basicModel) {
        console.log(`⚠️ ${series} 정확한 기본 모델 없음, 2차 선택 시도`);
        
        basicModel = devicesToChooseFrom.find(device => 
          !device.modelEn?.toLowerCase().includes('pro') && 
          !device.modelEn?.toLowerCase().includes('plus') && 
          !device.modelEn?.toLowerCase().includes('max') && 
          !device.modelEn?.toLowerCase().includes('mini') &&
          !device.modelEn?.toLowerCase().includes('2nd') &&
          !device.modelEn?.toLowerCase().includes('3rd') &&
          !device.modelEn?.toLowerCase().endsWith('e') &&
          !device.modelKo?.includes('프로') &&
          !device.modelKo?.includes('플러스') &&
          !device.modelKo?.includes('맥스') &&
          !device.modelKo?.includes('미니')
        );
        
        if (basicModel) {
          console.log(`🔍 ${series} 2차 기본 모델 선택: ${basicModel.modelKo} (${basicModel.modelEn})`);
        }
      }
      
      // 여전히 없으면 첫 번째 모델 사용
      if (!basicModel) {
        basicModel = devicesToChooseFrom[0];
        console.log(`🔄 ${series} 폴백 모델 사용: ${basicModel.modelKo} (${basicModel.modelEn})`);
      }
      
      // 최종 선택된 모델 정보 출력
      if (basicModel) {
        // 선택된 모델이 표준 모델인지 커스텀 모델인지 구분하여 로그 출력
        const isStandardModel = isValidIPhoneModel(basicModel.modelKo, basicModel.modelEn);
        if (isStandardModel) {
          console.log(`✅ ${series} 표준 대표 모델 최종 선택: ${basicModel.modelKo} (${basicModel.modelEn})`);
        } else {
          console.log(`✅ ${series} 커스텀 대표 모델 최종 선택: ${basicModel.modelKo} (사용자 추가 모델)`);
        }
      }
      
      return basicModel;
    };
    
    // 시리즈별로 그룹화
    const seriesGroups: Record<string, any[]> = {};
    firebaseAppleSmartphones.forEach(device => {
      if (!seriesGroups[device.series]) {
        seriesGroups[device.series] = [];
      }
      seriesGroups[device.series].push(device);
    });
    
    // 각 시리즈의 대표 모델 선택 및 이미지 결정 (매니페스트 리졸버 — 실존 파일 보장)
    Object.keys(seriesGroups).forEach(series => {
      const representativeDevice = getSeriesRepresentativeModel(series, seriesGroups[series]);
      if (representativeDevice) {
        const validImagePath = resolvePhoneImage({
          brand: 'Apple',
          series,
          modelEn: representativeDevice.modelEn,
          modelKo: representativeDevice.modelKo,
          catalogImage: representativeDevice.image
        });

        thumbnails[series] = validImagePath;
        console.log(`✅ ${series} 유효한 대표 모델 선택: ${representativeDevice.modelKo} (${representativeDevice.modelEn})`);
        console.log(`🖼️ ${series} 시리즈 최종 이미지: ${validImagePath}`);
      }
    });

    // 🔄 Firebase에 없는 시리즈는 시리즈 대표 이미지 사용 (실존 파일 보장)
    Object.keys(IPHONE_SERIES_THUMBNAILS).forEach(series => {
      if (!thumbnails[series]) {
        const validImagePath = resolvePhoneSeriesImage('Apple', series);

        thumbnails[series] = validImagePath;
        console.log(`🔄 ${series} 시리즈 폴백 이미지 사용: ${validImagePath}`);
      }
    });

    return thumbnails;
  }, [firebaseAppleSmartphones]);

  // 🔥 Firebase 기반 iPhone 모델 썸네일 (실시간) - 유효한 모델명만 필터링
  const firebaseIPhoneModelThumbnails = useMemo(() => {
    if (!firebaseAppleSmartphones.length) {
      // 🔄 Firebase 데이터가 없을 경우 상수 폴백 (리졸버로 실존 경로 보정)
      const fallback: Record<string, string> = {};
      Object.entries(IPHONE_MODEL_THUMBNAILS).forEach(([modelKo, img]) => {
        fallback[modelKo] = resolvePhoneImage({ brand: 'Apple', modelKo, catalogImage: img });
      });
      return fallback;
    }
    
    const thumbnails: Record<string, string> = {};
    
    // 올바른 iPhone 모델명 패턴 검증 함수 (시리즈 썸네일과 동일)
    const isValidIPhoneModel = (modelKo: string, modelEn: string) => {
      const validKoreanPatterns = [
        /^아이폰\s?(1[0-6]|[6-9]|SE|X[SR]?)\s?(프로|플러스|맥스|미니)?/i,
        /^iPhone\s?(1[0-6]|[6-9]|SE|X[SR]?)\s?(Pro|Plus|Max|Mini)?/i
      ];
      
      const validEnglishPatterns = [
        /^iPhone\s?(1[0-6]|[6-9]|SE|X[SR]?)\s?(Pro|Plus|Max|Mini)?/i
      ];
      
      const koreanValid = validKoreanPatterns.some(pattern => pattern.test(modelKo));
      const englishValid = validEnglishPatterns.some(pattern => pattern.test(modelEn || ''));
      
      return koreanValid || englishValid;
    };
    
    firebaseAppleSmartphones.forEach(device => {
      // 🔧 모든 Firebase 모델을 포함 — 매니페스트 리졸버로 실존 이미지 결정
      const validImagePath = resolvePhoneImage({
        brand: 'Apple',
        series: device.series,
        modelEn: device.modelEn,
        modelKo: device.modelKo,
        catalogImage: device.image
      });

      thumbnails[device.modelKo] = validImagePath;
      
      // 표준/커스텀 모델 구분하여 로그 출력
      if (isValidIPhoneModel(device.modelKo, device.modelEn)) {
        console.log(`📱 표준 iPhone 모델 썸네일: ${device.modelKo} (${device.modelEn})`);
      } else {
        console.log(`📱 커스텀 모델 썸네일: ${device.modelKo} (사용자 추가 모델)`);
      }
      console.log(`🖼️ ${device.modelKo} 최종 이미지: ${validImagePath}`);
    });
    
    // 🔄 Firebase에 없는 모델은 상수 폴백 사용 (리졸버로 실존 경로 보정)
    Object.keys(IPHONE_MODEL_THUMBNAILS).forEach(modelKo => {
      if (!thumbnails[modelKo]) {
        const validImagePath = resolvePhoneImage({
          brand: 'Apple',
          modelKo,
          catalogImage: IPHONE_MODEL_THUMBNAILS[modelKo]
        });

        thumbnails[modelKo] = validImagePath;
        console.log(`🔄 iPhone 모델 폴백 이미지 사용: ${modelKo} → ${validImagePath}`);
      }
    });
    
    return thumbnails;
  }, [firebaseAppleSmartphones]);


  // ✅ 최적화된 데이터 변환 훅으로 교체됨

  // 추천+최근+검색+서브카테고리 필터링 (태블릿/웨어러블) - Apple + Samsung 통합
  // 🎧 웨어러블도 Firebase 기반으로 전환 (2024.05)
  const filteredModels = category && category !== '스마트폰'
    ? [
        ...(recent.filter(m => m.name && m.name.includes(search))),
        ...(category === '태블릿' 
          ? [
              // 🔥 Firebase 기반 태블릿 모델 (추천 먼저) - Apple & Samsung
              ...convertedTabletModels.filter(m => {
                const matchesSearch = m.name.toLowerCase().includes(search.toLowerCase());
                const matchesSubcategory = selectedSubcategory === '전체' || m.subcategory === selectedSubcategory;
                const isNotDuplicate = !recent.some(r => r.name === m.name);
                return m.recommended && matchesSearch && matchesSubcategory && isNotDuplicate;
              }),
              // 🔥 Firebase 기반 태블릿 모델 (일반) - Apple & Samsung
              ...convertedTabletModels.filter(m => {
                const matchesSearch = m.name.toLowerCase().includes(search.toLowerCase());
                const matchesSubcategory = selectedSubcategory === '전체' || m.subcategory === selectedSubcategory;
                const isNotDuplicate = !recent.some(r => r.name === m.name);
                return !m.recommended && matchesSearch && matchesSubcategory && isNotDuplicate;
              })
            ]
          : [
                // 🎧 Firebase 기반 웨어러블 모델 (추천 먼저) - 웨어러블만 남은 경우
                ...(convertedWearableModels.length > 0 ? convertedWearableModels : DEVICE_MODELS['웨어러블']).filter(m => {
                  const matchesSearch = m.name.toLowerCase().includes(search.toLowerCase());
                  const isNotDuplicate = !recent.some(r => r.name === m.name);
                  return m.recommended && matchesSearch && isNotDuplicate;
                }),
                // 🎧 Firebase 기반 웨어러블 모델 (일반)
                ...(convertedWearableModels.length > 0 ? convertedWearableModels : DEVICE_MODELS['웨어러블']).filter(m => {
                  const matchesSearch = m.name.toLowerCase().includes(search.toLowerCase());
                  const isNotDuplicate = !recent.some(r => r.name === m.name);
                  return !m.recommended && matchesSearch && isNotDuplicate;
                })
              ]
        )
      ]
    : [];


  // 🔥 아이폰 시리즈 목록 (Firebase 전용)
  const iphoneSeriesList = useMemo(() => {
    const seriesModels = firebaseIPhoneSeriesModels; // Firebase 전용, 상수 폴백 제거
    const seriesThumbnails = firebaseIPhoneSeriesThumbnails; // Firebase 전용, 상수 폴백 제거
    
    console.log(`🔍 iPhone 시리즈 리스트 생성:`);
    console.log(`  - seriesModels 키: ${Object.keys(seriesModels)}`);
    console.log(`  - seriesThumbnails 키: ${Object.keys(seriesThumbnails)}`);
    
    let allSeries = Object.keys(seriesModels)
      .map(series => {
        const seriesImage = seriesThumbnails[series] || '';
        console.log(`🎯 ${series} 시리즈 UI 이미지 설정: ${seriesImage}`);
        return { name: series, image: seriesImage };
      });
    
    // 🔍 시리즈별 필터링 적용 (selectedSubcategory가 '전체'가 아닌 경우)
    if (selectedSubcategory !== '전체') {
      allSeries = allSeries.filter(s => s.name === selectedSubcategory);
    }
    
    // 검색어 필터링
    allSeries = allSeries.filter(s => s.name.toLowerCase().includes(search.toLowerCase()));
    
      // 최신순 정렬: iPhone 16, 15, 14 ... 그 외(SE 등)는 뒤로
    const finalSeries = allSeries.sort((a, b) => {
      const parse = (name: string) => {
        const m = name.match(/iPhone\s*(\d+)/i);
        if (m) return { group: 0, value: parseInt(m[1], 10) };
        if (/iPhone\s*SE\s*3/i.test(name)) return { group: 1, value: 3 };
        if (/iPhone\s*SE/i.test(name)) return { group: 1, value: 1 };
        return { group: 2, value: 0 };
      };
      const pa = parse(a.name);
      const pb = parse(b.name);
      if (pa.group !== pb.group) return pa.group - pb.group; // group 0 먼저
      return pb.value - pa.value; // 값 큰 것 먼저
    });
    
    console.log(`✅ 최종 iPhone 시리즈 리스트: ${finalSeries.length}개`);
    finalSeries.forEach(s => {
      console.log(`  - ${s.name}: ${s.image}`);
      // iPhone 15가 아닌 다른 시리즈에서 iPhone 15 이미지 사용 시 경고
      if (s.name !== 'iPhone 15' && s.image.includes('iphone15')) {
        console.warn(`⚠️ ${s.name} 시리즈에서 iPhone 15 이미지 사용됨: ${s.image}`);
      }
      // 빈 이미지 경로 경고
      if (!s.image || s.image.trim() === '') {
        console.warn(`⚠️ ${s.name} 시리즈 이미지 경로가 비어있음`);
      }
    });
    
    return finalSeries;
  }, [firebaseIPhoneSeriesModels, firebaseIPhoneSeriesThumbnails, search, selectedSubcategory]);

  // 🔥 아이폰 시리즈 내 모델 목록 (Firebase 전용)
  const iphoneModelsInSeries = useMemo(() => {
    if (!selectedIphoneSeries) return [];
    
    console.log(`🍎 iPhone 모델 조회: ${selectedIphoneSeries} 시리즈`);
    console.log(`🔍 Firebase 데이터 상태: ${firebaseAppleSmartphones.length}개`);
    
    const seriesModels = firebaseIPhoneSeriesModels; // Firebase 전용, 상수 폴백 제거
    const modelThumbnails = firebaseIPhoneModelThumbnails; // Firebase 전용, 상수 폴백 제거
    const seriesThumbnails = firebaseIPhoneSeriesThumbnails; // Firebase 전용, 상수 폴백 제거
    
    return (seriesModels[selectedIphoneSeries] || [])
        .filter(m => m.toLowerCase().includes(search.toLowerCase()))
      .map(modelName => ({ 
        name: modelName, 
        image: modelThumbnails[modelName] || (seriesThumbnails[selectedIphoneSeries] || '') 
      }));
  }, [selectedIphoneSeries, firebaseIPhoneSeriesModels, firebaseIPhoneModelThumbnails, firebaseIPhoneSeriesThumbnails, search]);

  // 🔥 아이폰 모델별 스토리지 옵션 (Firebase 전용)
  const iphoneStorageOptions = useMemo(() => {
    if (!selectedIphoneModel) return [];
    
    const storageByModel = firebaseIPhoneStorageByModel; // Firebase 전용, 상수 폴백 제거
    
    return storageByModel[selectedIphoneModel] || [];
  }, [selectedIphoneModel, firebaseIPhoneStorageByModel]);

  // 🔥 삼성 시리즈 데이터 (Firebase 전용)
  const samsungSeriesData = useMemo(() => {
    console.log(`🔍 Samsung 시리즈 데이터 생성: firebaseSamsungSeries.length = ${firebaseSamsungSeries.length}`);
    if (firebaseSamsungSeries.length === 0) {
      console.log('⚠️ firebaseSamsungSeries가 비어있음 - 빈 배열 반환');
    } else {
      console.log(`✅ Samsung 시리즈 데이터:`, firebaseSamsungSeries.map(s => s.name));
    }
    return firebaseSamsungSeries; // Firebase 전용, 상수 폴백 제거
  }, [firebaseSamsungSeries]);

  // 모델 선택 핸들러 (Apple + Samsung 통합)
  const handleModelSelect = (model: DeviceModel) => {
    setSelectedModel(model);
    
    // 🤖 Samsung 태블릿 특별 처리 (기존 로직과 호환성 유지)
    if (category === '태블릿' && selectedManufacturer === 'SAMSUNG') {
      // Samsung 태블릿의 경우 selectedSamsungTabletModel도 함께 설정
      setSelectedSamsungTabletModel(model.name); // model.name이 한글 모델명
      console.log(`🤖 Samsung 태블릿 모델 선택: ${model.name}`);
      setActiveStep(3); // 용량 + 연결성 선택 단계로 이동
      return;
    }
    
    // 웨어러블 카테고리에서 AirPods 모델이면 구성 선택으로 진행
    if (category && needsConfigurationSelection(category, model.name)) {
      setActiveStep(2);
    } 
    // 태블릿(애플): 세부 모델 화면은 다음 단계(3)
    else if (category === '태블릿' && model.subModels && model.subModels.length > 0) {
      setActiveStep(3);
    }
    // 기타 카테고리(기존 로직 유지)
    else if (model.subModels && model.subModels.length > 0) {
      setActiveStep(2);
    }
  };

  // 아이폰 시리즈/모델/용량 선택 핸들러
  const handleIphoneSeriesSelect = (series: string) => {
    setSelectedIphoneSeries(series);
    setSelectedIphoneModel('');
    setSelectedIphoneStorage('');
    setCustomIphoneStorage('');
    // 시리즈 선택 즉시 모델 선택 단계로 이동
    setActiveStep(3);
  };

  const handleIphoneModelSelect = (modelName: string) => {
    setSelectedIphoneModel(modelName);
    // 모델 선택 즉시 구성(용량) 선택 단계로 이동
    setActiveStep(4);
  };

  const handleIphoneStorageSelect = (storage: string) => {
    setSelectedIphoneStorage(storage);
    setCustomIphoneStorage('');
    // 용량 선택 즉시 등급 선택 단계로 이동
    setActiveStep(5);
  };

  // 세부 모델 선택 핸들러
  const handleSubModelSelect = (subModel: SubModel) => {
    setSelectedSubModel(subModel);
    // 태블릿(애플) 세부모델 선택 시 등급 선택 단계로 이동
    if (category === '태블릿' && selectedManufacturer === 'APPLE') {
      setActiveStep(4);
    }
  };

  // 구성 선택 핸들러 → 등급 선택 단계로 이동
  const handleConfigurationSelect = (configuration: DeviceConfiguration) => {
    setSelectedConfiguration(configuration);
    setActiveStep(3);
  };

  // 키보드 내비게이션 지원
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && activeStep === 0 && category) setActiveStep(1);
    if (e.key === 'Escape') onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth 
      fullScreen={isMobile} // 모바일에서는 전체 화면
      scroll="paper"
      onKeyDown={handleKeyDown}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 6, // 모바일에서는 모서리 둥글게 제거
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px) saturate(180%)',
          border: isMobile ? 'none' : '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: isMobile 
            ? 'none' 
            : '0 32px 64px rgba(0, 0, 0, 0.12), 0 8px 32px rgba(0, 0, 0, 0.08)',
          overflowY: 'auto',
          height: isMobile ? '100vh' : 'auto', // 모바일에서는 전체 높이
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(14, 165, 233, 0.1) 100%)',
        backdropFilter: 'blur(20px)',
        p: isMobile ? 2 : 4, // 모바일에서는 패딩 축소
        textAlign: 'center',
        borderBottom: '1px solid rgba(99, 102, 241, 0.1)',
        flexShrink: 0, // 크기 고정
      }}>
        <Typography variant={isMobile ? "h6" : "h5"} fontWeight={800} sx={{
          background: 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          mb: isMobile ? 1 : 2
        }}>
          {isQuoteMode ? '시세 조회' : '반납 기기 추가'}
        </Typography>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ 
          '& .MuiStepLabel-root': {
            '& .MuiStepLabel-label': {
              fontWeight: 600,
              fontSize: isMobile ? '0.8rem' : '0.9rem'
            }
          }
        }}>
          {steps.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </DialogTitle>
      
      <DialogContent sx={{
        // 모바일에서는 남은 공간을 모두 사용하고 스크롤 가능하게
        flex: 1,
        overflow: 'auto',
        p: isMobile ? 2 : 3,
        background: 'linear-gradient(135deg, rgba(248, 250, 252, 0.8) 0%, rgba(241, 245, 249, 0.8) 100%)',
        // 모바일에서는 최소 높이 제거
        minHeight: isMobile ? 'auto' : 450,
      }}>
        <SafeErrorBoundary stepName="기기 선택 다이얼로그">
        {activeStep === 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" fontWeight={700} mb={3} textAlign="center" color="#374151">
              {isQuoteMode ? '어떤 기기의 시세를 확인할까요?' : '어떤 기기를 반납하시나요?'}
            </Typography>
            <Grid container spacing={3} justifyContent="center">
              {['태블릿', '웨어러블', '스마트폰'].map(cat => (
                <Grid item key={cat} xs={12} sm={4}>
                  <Card
                    onClick={() => { setCategory(cat as DeviceCategory); setActiveStep(1); setTimeout(() => searchRef.current?.focus(), 200); }}
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      background: category === cat 
                        ? 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)' 
                        : 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(10px)',
                      border: category === cat ? '2px solid #6366f1' : '1px solid rgba(226, 232, 240, 0.8)',
                      borderRadius: 4,
                      boxShadow: category === cat 
                        ? '0 20px 40px rgba(99, 102, 241, 0.2), 0 8px 16px rgba(99, 102, 241, 0.1)' 
                        : '0 4px 12px rgba(0, 0, 0, 0.05)',
                      position: 'relative',
                      outline: category === cat ? '2px solid #6366f1' : 'none',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: category === cat
                          ? 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)'
                          : 'transparent',
                        transition: 'all 0.3s ease',
                        borderTopLeftRadius: 4,
                        borderTopRightRadius: 4,
                      },
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 20px 40px rgba(99, 102, 241, 0.15), 0 8px 16px rgba(99, 102, 241, 0.1)',
                        background: category === cat 
                          ? 'linear-gradient(135deg, #5b21b6 0%, #0284c7 100%)' 
                          : 'rgba(255, 255, 255, 0.95)',
                        borderColor: category === cat ? '#6366f1' : 'rgba(99, 102, 241, 0.35)',
                        outline: category === cat ? '2px solid #6366f1' : '2px solid rgba(99, 102, 241, 0.35)',
                      },
                      '&:hover::before': {
                        background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)',
                      }
                    }}
                  >
                    <CardContent sx={{ 
                      textAlign: 'center', 
                      py: 4,
                      color: category === cat ? '#fff' : '#374151'
                    }}>
                      <Box sx={{ 
                        mb: 2, 
                        color: category === cat ? '#fff' : '#6366f1',
                        display: 'flex',
                        justifyContent: 'center'
                      }}>
                        {categoryIcons[cat as DeviceCategory]}
                      </Box>
                      <Typography variant="h6" fontWeight={700}>
                        {cat}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
        
        {activeStep === 1 && category && (
          <Box sx={{ mt: 2 }}>
            {(category === '스마트폰' || category === '태블릿') ? (
              // 제조사 선택 (스마트폰/태블릿 공통)
              <>
                <Typography variant="h6" fontWeight={700} mb={2} textAlign="center" color="#374151">
                  제조사를 선택하세요
                </Typography>
                <Grid container spacing={3} justifyContent="center">
                  {[
                    { 
                      key: 'APPLE', 
                      label: 'APPLE', 
                      desc: category === '태블릿' ? '아이패드(iPad)시리즈' : '아이폰(iPhone) 시리즈', 
                      color: '#111827' 
                    }, // 검정
                    { 
                      key: 'SAMSUNG', 
                      label: 'SAMSUNG', 
                      desc: category === '태블릿' ? '갤럭시 탭(Galaxy Tab)시리즈' : '갤럭시(Galaxy) 시리즈', 
                      color: '#003DA5' 
                    }, // 진한 파랑
                  ].map((m) => (
                    <Grid item xs={6} sm={5} md={4} key={m.key}>
                      <Card
                        onClick={() => { setSelectedManufacturer(m.key as 'APPLE' | 'SAMSUNG'); setActiveStep(2); }}
                        sx={{
                          cursor: 'pointer',
                          p: 3,
                          minHeight: 140,
                          borderRadius: 3,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          gap: 0.5,
                          border: selectedManufacturer === m.key ? `2px solid ${m.color}` : '1px solid #e5e7eb',
                          background: selectedManufacturer === m.key ? 'rgba(14,165,233,0.06)' : 'white',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            border: `2px solid ${m.color}`,
                            transform: 'translateY(-2px)'
                          }
                        }}
                      >
                        <Typography variant="h5" fontWeight={900} sx={{ color: m.color }}>
                          {m.label}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {m.desc}
                        </Typography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            ) : (
              // 기존: 태블릿/웨어러블 검색+선택 화면
              <>
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    inputRef={searchRef}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="모델명을 검색하세요..."
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#6366f1' }} />
                        </InputAdornment>
                      ),
                      sx: {
                        borderRadius: 3,
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(99, 102, 241, 0.2)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(99, 102, 241, 0.4)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#6366f1',
                        },
                      }
                    }}
                    autoFocus
                  />
                  <Button
                    variant="outlined"
                    onClick={() => setActiveStep(0)}
                    sx={{ 
                      minWidth: 80, 
                      fontWeight: 700,
                      borderRadius: 3,
                      borderColor: '#6366f1',
                      color: '#6366f1',
                      '&:hover': {
                        borderColor: '#5b21b6',
                        background: 'rgba(99, 102, 241, 0.05)',
                      }
                    }}
                  >
                    이전
                  </Button>
                </Box>
                {/* 태블릿 서브카테고리 필터 (웨어러블 단계에서는 표시되지 않음) */}
                {false && category === '태블릿' && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} mb={2} color="#374151">
                      iPad 모델 필터
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {['전체', ...TABLET_SUBCATEGORIES].map(subcategory => (
                        <Chip
                          key={subcategory}
                          label={subcategory}
                          onClick={() => setSelectedSubcategory(subcategory)}
                          variant={selectedSubcategory === subcategory ? 'filled' : 'outlined'}
                          sx={{
                            fontWeight: 600,
                            borderRadius: 3,
                            background: selectedSubcategory === subcategory 
                              ? 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)'
                              : 'rgba(255, 255, 255, 0.8)',
                            color: selectedSubcategory === subcategory ? '#fff' : '#6366f1',
                            border: selectedSubcategory === subcategory ? 'none' : '1px solid #6366f1',
                            '&:hover': {
                              background: selectedSubcategory === subcategory 
                                ? 'linear-gradient(135deg, #5b21b6 0%, #0284c7 100%)'
                                : 'rgba(99, 102, 241, 0.1)',
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
                <Grid container spacing={3}>
                  {category === '웨어러블' && filteredModels.map(model => (
                    <Grid item xs={6} sm={4} md={3} key={model.name}>
                      <DeviceModelCard
                        model={model}
                        selected={selectedModel?.name === model.name}
                        onClick={() => handleModelSelect(model)}
                      />
                    </Grid>
                  ))}
                  {/* 직접 입력 카드 (스마트폰 제외) */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Card
                      sx={{
                        border: '2px dashed #6366f1',
                        borderRadius: 4,
                        background: 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        transition: 'all 0.2s',
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.95)',
                          transform: 'translateY(-2px)',
                        }
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center', p: 3 }}>
                        <Typography fontWeight={700} color="#6366f1" mb={2}>
                          찾는 모델이 없나요?
                        </Typography>
                        <TextField
                          value={customModel}
                          onChange={e => setCustomModel(e.target.value)}
                          placeholder="직접 입력하세요"
                          fullWidth
                          size="small"
                          sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                            }
                          }}
                        />
                        <Button
                          variant="contained"
                          sx={{ 
                            fontWeight: 700,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #5b21b6 0%, #0284c7 100%)',
                            }
                          }}
                          disabled={!customModel}
                          onClick={() => {
                            // 🎧 직접 입력된 모델은 가격 정보 없이 추가 (검수 후 확정)
                            handleWearableDeviceAdd({ 
                              deviceType: category, 
                              deviceModel: customModel,
                              estimatedPrice: null,
                              allGradePrices: null,
                            });
                          }}
                        >
                          직접 추가
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
        )}
        {/* 태블릿(애플): 모델 선택 화면 (제조사 선택 이후) */}
        {activeStep === 2 && category === '태블릿' && selectedManufacturer === 'APPLE' && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                inputRef={searchRef}
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="모델명을 검색하세요..."
                variant="outlined"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#6366f1' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(99, 102, 241, 0.2)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(99, 102, 241, 0.4)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366f1',
                    },
                  }
                }}
                autoFocus
              />
            </Box>
            {/* 태블릿 서브카테고리 필터 (브랜드별) */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} mb={2} color="#374151">
                {(selectedManufacturer as string) === 'SAMSUNG' ? 'Galaxy Tab 모델 필터' : 'iPad 모델 필터'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {['전체', ...((selectedManufacturer as string) === 'SAMSUNG' 
                  ? SAMSUNG_TABLET_SUBCATEGORIES 
                  : (category === '태블릿' ? firebaseTabletSubcategories : TABLET_SUBCATEGORIES)
                )].map(subcategory => (
                  <Chip
                    key={subcategory}
                    label={subcategory}
                    onClick={() => setSelectedSubcategory(subcategory)}
                    variant={selectedSubcategory === subcategory ? 'filled' : 'outlined'}
                    sx={{
                      fontWeight: 600,
                      borderRadius: 3,
                      background: selectedSubcategory === subcategory 
                        ? 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)'
                        : 'rgba(255, 255, 255, 0.8)',
                      color: selectedSubcategory === subcategory ? '#fff' : '#6366f1',
                      border: selectedSubcategory === subcategory ? 'none' : '1px solid #6366f1',
                      '&:hover': {
                        background: selectedSubcategory === subcategory 
                          ? 'linear-gradient(135deg, #5b21b6 0%, #0284c7 100%)'
                          : 'rgba(99, 102, 241, 0.1)',
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
            <Grid container spacing={3}>
              
              {/* 정상 모델 표시 */}
              {filteredModels.map(model => (
                <Grid item xs={6} sm={4} md={3} key={model.name}>
                  <DeviceModelCard
                    model={model}
                    selected={selectedModel?.name === model.name}
                    onClick={() => handleModelSelect(model)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* 태블릿(삼성): 모델 선택 화면 */}
        {activeStep === 2 && category === '태블릿' && selectedManufacturer === 'SAMSUNG' && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="모델명을 검색하세요..."
                variant="outlined"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#6366f1' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(99, 102, 241, 0.2)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(99, 102, 241, 0.4)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                  }
                }}
              />
            </Box>
            {/* 🔥 시리즈별 필터 (Samsung Galaxy Tab 전용) */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} mb={2} color="#374151">
                Galaxy Tab 시리즈 필터
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {['전체', ...SAMSUNG_TABLET_SUBCATEGORIES].map(subcategory => (
                  <Chip
                    key={subcategory}
                    label={subcategory}
                    onClick={() => setSelectedSubcategory(subcategory)}
                    variant={selectedSubcategory === subcategory ? 'filled' : 'outlined'}
                    sx={{
                      fontWeight: 600,
                      borderRadius: 3,
                      background: selectedSubcategory === subcategory 
                        ? 'linear-gradient(135deg, #1428A0 0%, #0070f3 100%)'  // Samsung Blue
                        : 'rgba(255, 255, 255, 0.8)',
                      color: selectedSubcategory === subcategory ? '#fff' : '#1428A0',
                      border: selectedSubcategory === subcategory ? 'none' : '1px solid #1428A0',
                      '&:hover': {
                        background: selectedSubcategory === subcategory 
                          ? 'linear-gradient(135deg, #0c1f7a 0%, #0056b3 100%)'
                          : 'rgba(20, 40, 160, 0.1)',
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
            
            <Grid container spacing={3}>
              {/* 🔥 Firebase 기반 Samsung 태블릿 모델 표시 */}
              {filteredModels.map(model => (
                <Grid item xs={6} sm={4} md={3} key={model.name}>
                  <DeviceModelCard
                    model={model}
                    selected={selectedModel?.name === model.name}
                    onClick={() => handleModelSelect(model)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* 태블릿(애플): 삼성과 동일한 섹션 레이아웃으로 통일 */}
        {activeStep === 3 && category === '태블릿' && selectedManufacturer === 'APPLE' && selectedModel && (
          <Box sx={{ mt: 2 }}>
            {/* 상단 안내 타이틀 (삼성과 동일 문구/스타일) */}
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#374151', mb: 1 }}>
                {selectedModel.name} 용량과 연결 방식을 선택하세요
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                저장 용량과 연결 방식(Wi‑Fi/Cellular)을 모두 선택해주세요.
              </Typography>
            </Box>
            {/* 섹션A: 연결 방식 */}
            <Box sx={{ mb: 3 }}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.06)', background: '#fff', overflow: 'visible', position: 'relative' }}>
                <CardContent sx={{ p: 2, overflow: 'visible' }}>
                  <Typography variant="h6" fontWeight={800} sx={{ color: '#111827' }}>연결 방식 선택</Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.25 }}>
                    사용 중인 모델의 네트워크 유형을 선택하세요.
                  </Typography>
                  <Box sx={{ mt: 1, overflowX: 'visible', overflowY: 'visible', pb: 0.25, '&::-webkit-scrollbar': { display: 'none' }, scrollSnapType: 'x mandatory', position: 'relative', zIndex: 0 }}>
                    <Box sx={{ display: 'flex', gap: 1.25, minWidth: 0, py: 0 }}>
                      {(['Wi-Fi', 'Wi-Fi + Cellular'] as const).map((label, idx) => (
                        <Box key={label} sx={{ minWidth: 200 }}>
                          <ConnectivityOptionCard
                            label={label}
                            selected={selectedAppleTabletConnectivity === label}
                            onClick={() => setSelectedAppleTabletConnectivity(label)}
                            index={idx}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ my: 2, height: 1, background: 'linear-gradient(90deg, rgba(0,0,0,0), rgba(0,0,0,0.08), rgba(0,0,0,0))' }} />

            {/* 섹션B: 용량 */}
            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.06)', background: '#fff' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h6" fontWeight={800} sx={{ color: '#111827' }}>용량 선택</Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.25 }}>
                  보유하신 저장 용량을 선택하거나 직접 입력하세요.
                </Typography>
                <Grid container spacing={2} justifyContent="flex-start" sx={{ mt: 0.25 }}>
                  {Array.from(new Set((selectedModel.subModels || []).map(sm => sm.storage || '기본'))).map((storage, index) => (
                    <Grid item xs={6} sm={4} md={3} key={`${selectedModel.name}-${storage}`}>
                      <StorageOptionCard
                        label={storage}
                        selected={selectedAppleTabletStorage === storage}
                        onClick={() => { setSelectedAppleTabletStorage(storage); }}
                        index={index}
                      />
                    </Grid>
                  ))}
                  {/* 동적 추가된 용량 카드들 */}
                  {customAppleTabletStorages.map((storage, index) => (
                    <Grid item xs={6} sm={4} md={3} key={`custom-${storage}`}>
                      <DeletableStorageCard
                        label={storage}
                        selected={selectedAppleTabletStorage === storage}
                        onClick={() => { setSelectedAppleTabletStorage(storage); }}
                        onDelete={() => {
                          setCustomAppleTabletStorages(prev => prev.filter(s => s !== storage));
                          if (selectedAppleTabletStorage === storage) {
                            setSelectedAppleTabletStorage('');
                          }
                        }}
                        index={index + Array.from(new Set((selectedModel.subModels || []).map(sm => sm.storage || '기본'))).length}
                      />
                    </Grid>
                  ))}
                  <Grid item xs={8} sm={5} md={3}>
                    <Card
                      sx={{
                        border: '2px dashed #6366f1',
                        borderRadius: 3,
                        background: 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        height: 'auto',
                        minHeight: 120,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        transition: 'all 0.2s',
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.95)',
                          transform: 'translateY(-2px)',
                        }
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center', p: 2 }}>
                        <Typography fontWeight={700} color="#6366f1" mb={1.5} sx={{ fontSize: '0.9rem' }}>
                          다른 용량이 있나요?
                        </Typography>
                        <TextField
                          value={customAppleTabletStorage}
                          onChange={e => setCustomAppleTabletStorage(e.target.value)}
                          placeholder="직접 입력하세요"
                          fullWidth
                          size="small"
                          sx={{
                            mb: 1.5,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                            }
                          }}
                        />
                        <Button
                          variant="contained"
                          size="small"
                          sx={{ 
                            fontWeight: 700,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #5b21b6 0%, #0284c7 100%)',
                            }
                          }}
                          disabled={!customAppleTabletStorage.trim()}
                          onClick={() => {
                            const trimmed = customAppleTabletStorage.trim();
                            if (!trimmed) return;
                            // 중복 방지
                            const allStorages = [...Array.from(new Set((selectedModel.subModels || []).map(sm => sm.storage || '기본'))), ...customAppleTabletStorages];
                            if (allStorages.includes(trimmed)) return;
                            // 새 용량 카드 추가
                            setCustomAppleTabletStorages(prev => [...prev, trimmed]);
                            setCustomAppleTabletStorage('');
                            // 자동 선택
                            setSelectedAppleTabletStorage(trimmed);
                          }}
                        >
                          직접 추가
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* 스마트폰: 제조사 선택 후 시리즈 단계 */}
        {activeStep === 2 && category === '스마트폰' && (
          <Box sx={{ mt: 2 }}>
            {selectedManufacturer === 'APPLE' ? (
              <Box>
                {/* 🍎 iPhone 시리즈별 필터 (Apple Black 테마) */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} mb={2} color="#374151">
                    iPhone 시리즈 필터
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {['전체', ...IPHONE_SERIES_SUBCATEGORIES].map(subcategory => (
                      <Chip
                        key={subcategory}
                        label={subcategory}
                        onClick={() => {
                          setSelectedSubcategory(subcategory);
                          // 필터 선택 시 해당 시리즈 자동 선택 및 모델 화면으로 이동
                          if (subcategory !== '전체') {
                            // 필터와 매칭되는 시리즈 찾기
                            const matchedSeries = iphoneSeriesList.find(series => 
                              series.name === subcategory
                            );
                            if (matchedSeries) {
                              handleIphoneSeriesSelect(matchedSeries.name);
                            }
                          } else {
                            // "전체" 선택 시 시리즈 선택 해제 및 시리즈 목록 화면 유지
                            setSelectedIphoneSeries('');
                          }
                        }}
                        variant={selectedSubcategory === subcategory ? 'filled' : 'outlined'}
                        sx={{
                          fontWeight: 600,
                          borderRadius: 3,
                          background: selectedSubcategory === subcategory 
                            ? 'linear-gradient(135deg, #000000 0%, #1d1d1f 100%)'  // Apple Black
                            : 'rgba(255, 255, 255, 0.8)',
                          color: selectedSubcategory === subcategory ? '#fff' : '#000000',
                          border: selectedSubcategory === subcategory ? 'none' : '1px solid #000000',
                          '&:hover': {
                            background: selectedSubcategory === subcategory 
                              ? 'linear-gradient(135deg, #1d1d1f 0%, #333335 100%)'
                              : 'rgba(0, 0, 0, 0.1)',
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
                
                {/* 🔥 Firebase 기반 iPhone 시리즈 표시 (필터가 "전체"일 때만 표시) */}
                {selectedSubcategory === '전체' && (
                  <Grid container spacing={3}>
                    {iphoneSeriesList.map(series => (
                      <Grid item xs={6} sm={4} md={3} key={series.name}>
                        <DeviceModelCard
                          model={{ name: series.name, image: series.image }}
                          selected={selectedIphoneSeries === series.name}
                          onClick={() => handleIphoneSeriesSelect(series.name)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            ) : (
              <Box>
                {/* 🤖 Galaxy 시리즈별 필터 (Samsung Blue 테마) */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} mb={2} color="#374151">
                    Galaxy 시리즈 필터
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {['전체', ...GALAXY_SERIES_SUBCATEGORIES].map(subcategory => (
                      <Chip
                        key={subcategory}
                        label={subcategory}
                        onClick={() => {
                          setSelectedSubcategory(subcategory);
                          // 필터 선택 시 해당 시리즈 자동 선택 및 모델 화면으로 이동
                          if (subcategory !== '전체') {
                            // 필터와 매칭되는 시리즈 찾기
                            const matchedSeries = samsungSeriesData.find(series => 
                              series.name === subcategory
                            );
                            if (matchedSeries) {
                              setSelectedSamsungSeries(matchedSeries.name);
                              setActiveStep(3); // 바로 모델 선택 화면으로 이동
                            }
                          } else {
                            // "전체" 선택 시 시리즈 선택 해제 및 시리즈 목록 화면 유지
                            setSelectedSamsungSeries('');
                          }
                        }}
                        variant={selectedSubcategory === subcategory ? 'filled' : 'outlined'}
                        sx={{
                          fontWeight: 600,
                          borderRadius: 3,
                          background: selectedSubcategory === subcategory 
                            ? 'linear-gradient(135deg, #1428A0 0%, #0070f3 100%)'  // Samsung Blue
                            : 'rgba(255, 255, 255, 0.8)',
                          color: selectedSubcategory === subcategory ? '#fff' : '#1428A0',
                          border: selectedSubcategory === subcategory ? 'none' : '1px solid #1428A0',
                          '&:hover': {
                            background: selectedSubcategory === subcategory 
                              ? 'linear-gradient(135deg, #0c1f7a 0%, #0056b3 100%)'
                              : 'rgba(20, 40, 160, 0.1)',
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
                
                {/* 🔥 Firebase 기반 Samsung 시리즈 표시 (필터가 "전체"일 때만 표시) */}
                {selectedSubcategory === '전체' && (
                  <Grid container spacing={3}>
                    {samsungSeriesData
                      .filter(series => {
                        const seriesNameLower = series.name.toLowerCase();
                        // 기본: Galaxy 시리즈만 표시
                        return seriesNameLower.includes('galaxy');
                      })
                      .map(series => (
                        <Grid item xs={6} sm={4} md={3} key={series.name}>
                          <DeviceModelCard
                            model={{ name: series.name, image: resolvePhoneSeriesImage('Samsung', series.name) }}
                            selected={selectedSamsungSeries === series.name}
                            onClick={() => { setSelectedSamsungSeries(series.name); setActiveStep(3); }}
                          />
                        </Grid>
                      ))}
                  </Grid>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* 스마트폰(아이폰): 모델 선택 단계 */}
        {activeStep === 3 && category === '스마트폰' && selectedManufacturer === 'APPLE' && selectedIphoneSeries && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              {iphoneModelsInSeries.map(model => (
                <Grid item xs={6} sm={4} md={3} key={model.name}>
                  <DeviceModelCard
                    model={model}
                    selected={selectedIphoneModel === model.name}
                    onClick={() => handleIphoneModelSelect(model.name)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* 스마트폰(삼성): 모델 선택 단계 */}
        {activeStep === 3 && category === '스마트폰' && selectedManufacturer === 'SAMSUNG' && selectedSamsungSeries && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="모델명을 검색하세요..."
                variant="outlined"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#6366f1' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(99, 102, 241, 0.2)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(99, 102, 241, 0.4)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                  }
                }}
              />
            </Box>
            <Grid container spacing={3}>
              {samsungSeriesData.find(s => s.name === selectedSamsungSeries)?.models
                .filter(m => (m.name + m.name_ko).toLowerCase().includes(search.toLowerCase()))
                .sort((a, b) => {
                  const seriesName = selectedSamsungSeries;
                  const ga = getSamsungModelGeneration(seriesName, a.name);
                  const gb = getSamsungModelGeneration(seriesName, b.name);
                  if (ga !== gb) return gb - ga; // 세대 큰 것(최신) 먼저
                  const va = getSamsungVariantPriority(a.name);
                  const vb = getSamsungVariantPriority(b.name);
                  return vb - va; // 변형 우선순위 높은 것 먼저 (Ultra>Pro/Plus>기본>FE)
                })
                .map(m => (
                  <Grid item xs={6} sm={4} md={3} key={m.name}>
                    <DeviceModelCard
                      model={{
                        name: m.name_ko,
                        image: resolvePhoneImage({
                          brand: 'Samsung',
                          series: selectedSamsungSeries,
                          modelEn: m.name,
                          modelKo: m.name_ko
                        })
                      }}
                      selected={selectedSamsungModel === m.name}
                      onClick={() => { setSelectedSamsungModel(m.name); setActiveStep(4); }}
                    />
                  </Grid>
                ))}
            </Grid>
          </Box>
        )}

        {/* 구성 선택 단계 (웨어러블 AirPods) / 아이폰 용량 선택 */}
        {activeStep === 2 && selectedModel && category && needsConfigurationSelection(category, selectedModel.name) && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#374151', mb: 1 }}>
                {selectedModel.name} 구성을 선택하세요
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.6 }}>
                보유하고 계신 구성품에 맞춰 선택해주세요.<br/>
                <strong>검수 시 직원이 실제 모델을 확인</strong>하니 정확한 구성만 선택하시면 됩니다.
              </Typography>
            </Box>
            
            <Grid container spacing={3} justifyContent="center">
              {AIRPODS_CONFIGURATIONS.map((configuration, index) => (
                <Grid item xs={6} sm={4} md={3} key={configuration.id}>
                  <ConfigurationCard
                    configuration={configuration}
                    selected={selectedConfiguration?.id === configuration.id}
                    onClick={() => handleConfigurationSelect(configuration)}
                    index={index}
                  />
                </Grid>
              ))}
              
              {/* 직접 입력 카드 */}
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    border: '2px dashed #6366f1',
                    borderRadius: 4,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    transition: 'all 0.2s',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.95)',
                      transform: 'translateY(-2px)',
                    }
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Typography fontWeight={700} color="#6366f1" mb={2}>
                      다른 구성이 있나요?
                    </Typography>
                    <TextField
                      value={customConfiguration}
                      onChange={e => setCustomConfiguration(e.target.value)}
                      placeholder="직접 입력하세요"
                      fullWidth
                      size="small"
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                    <Button
                      variant="contained"
                      sx={{ 
                        fontWeight: 700,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5b21b6 0%, #0284c7 100%)',
                        }
                      }}
                      disabled={!customConfiguration}
                      onClick={() => {
                        if (selectedModel && category && customConfiguration) {
                          // 🎧 직접 입력된 구성은 가격 정보 없이 추가 (검수 후 확정)
                          handleWearableDeviceAdd({ 
                            deviceType: category, 
                            deviceModel: `${selectedModel.name} - ${customConfiguration}`,
                            // 직접 입력 시에는 가격 정보 없음 (검수 후 알림 예정)
                            estimatedPrice: null,
                            allGradePrices: allGradePrices,
                          });
                        }
                      }}
                    >
                      직접 추가
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* 아이폰: 용량 선택 */}
        {activeStep === 4 && category === '스마트폰' && selectedManufacturer === 'APPLE' && selectedIphoneModel && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#374151', mb: 1 }}>
                {selectedIphoneModel} 용량을 선택하세요
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                보유하신 저장 용량을 선택하거나 기타를 통해 직접 입력할 수 있습니다.
              </Typography>
            </Box>

            <Grid container spacing={2} justifyContent="flex-start">
              {/* 🎯 관리자 설정 용량만 표시 (관리자 페이지와 동일 목록) */}
              {smartIPhoneStorageOptions.storageOptionsWithStatus.map((option, index) => (
                <Grid item xs={6} sm={4} md={3} key={option.storage}>
                  <StorageOptionCard
                    label={option.storage}
                    selected={selectedIphoneStorage === option.storage}
                    onClick={() => handleIphoneStorageSelect(option.storage)}
                    index={index}
                  />
                </Grid>
              ))}

              {/* 기타(직접입력) 입력 박스 - 통일된 스타일(제목+입력+추가 버튼) */}
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    border: '2px dashed #6366f1',
                    borderRadius: 4,
                    background: 'rgba(255, 255, 255, 0.8)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Typography fontWeight={700} color="#6366f1" mb={2}>
                      기타 용량 직접 입력
                    </Typography>
                    <TextField
                      value={customIphoneStorage}
                      onChange={e => { 
                        setCustomIphoneStorage(e.target.value); 
                        setSelectedIphoneStorage(''); 
                      }}
                      placeholder="예: 1TB"
                      fullWidth
                      size="small"
                    />
                    <Button
                      variant="contained"
                      sx={{ mt: 1.5, fontWeight: 700 }}
                      disabled={!customIphoneStorage.trim()}
                      onClick={() => {
                        if (customIphoneStorage.trim()) {
                          setActiveStep(5);
                        }
                      }}
                    >
                      추가
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* 삼성: 용량 선택 */}
        {activeStep === 4 && category === '스마트폰' && selectedManufacturer === 'SAMSUNG' && selectedSamsungModel && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#374151', mb: 1 }}>
                {(samsungSeriesData.find(s => s.name === selectedSamsungSeries)?.models.find(m => m.name === selectedSamsungModel)?.name_ko || selectedSamsungModel)} 용량을 선택하세요
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                보유하신 저장 용량을 선택하거나 기타를 통해 직접 입력할 수 있습니다.
              </Typography>
            </Box>
            
            <Grid container spacing={2} justifyContent="flex-start">
              {/* 🎯 관리자 설정 용량만 표시 (관리자 페이지와 동일 목록) */}
              {smartSamsungStorageOptions.storageOptionsWithStatus.map((option, index) => (
                <Grid item xs={6} sm={4} md={3} key={option.storage}>
                  <StorageOptionCard
                    label={option.storage}
                    selected={selectedSamsungStorage === option.storage}
                    onClick={() => { setSelectedSamsungStorage(option.storage); setCustomSamsungStorage(''); setActiveStep(5); }}
                    index={index}
                  />
                </Grid>
              ))}
              {/* 기타(직접입력) - 통일된 스타일(제목+입력+추가 버튼) */}
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ border: '2px dashed #6366f1', borderRadius: 4, background: 'rgba(255, 255, 255, 0.8)', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Typography fontWeight={700} color="#6366f1" mb={2}>기타 용량 직접 입력</Typography>
                    <TextField
                      value={customSamsungStorage}
                      onChange={e => { setCustomSamsungStorage(e.target.value); setSelectedSamsungStorage(''); }}
                      placeholder="예: 1TB"
                      fullWidth
                      size="small"
                    />
                    <Button
                      variant="contained"
                      sx={{ mt: 1.5, fontWeight: 700 }}
                      disabled={!customSamsungStorage.trim()}
                      onClick={() => {
                        if (customSamsungStorage.trim()) { setActiveStep(5); }
                      }}
                    >
                      추가
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* 태블릿(삼성): 용량 + 연결성 선택 */}
        {activeStep === 3 && category === '태블릿' && selectedManufacturer === 'SAMSUNG' && selectedSamsungTabletModel && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#374151', mb: 1 }}>
                {selectedSamsungTabletModel} 용량과 연결 방식을 선택하세요
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                저장 용량과 연결 방식(Wi‑Fi/Cellular)을 모두 선택해주세요.
              </Typography>
            </Box>
            {/* 섹션A: 연결 방식 */}
            <Box sx={{ mb: 3 }}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.06)', background: '#fff', overflow: 'visible', position: 'relative' }}>
                <CardContent sx={{ p: 2, overflow: 'visible' }}>
                  <Typography variant="h6" fontWeight={800} sx={{ color: '#111827' }}>연결 방식 선택</Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.25 }}>
                    사용 중인 모델의 네트워크 유형을 선택하세요.
                  </Typography>
                  <Box sx={{ mt: 1, overflowX: 'visible', overflowY: 'visible', pb: 0.25, '&::-webkit-scrollbar': { display: 'none' }, scrollSnapType: 'x mandatory', position: 'relative', zIndex: 0 }}>
                    <Box sx={{ display: 'flex', gap: 1.25, position: 'relative', py: 0 }}>
                      {(['Wi-Fi', 'Wi-Fi + Cellular'] as const).map((label, idx) => (
                        <Box key={label} sx={{ minWidth: 200 }}>
                          <ConnectivityOptionCard
                            label={label}
                            selected={selectedSamsungTabletConnectivity === label}
                            onClick={() => setSelectedSamsungTabletConnectivity(label)}
                            index={idx}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
            {/* 섹션 구분선 */}
            <Box sx={{ my: 2, height: 1, background: 'linear-gradient(90deg, rgba(0,0,0,0), rgba(0,0,0,0.08), rgba(0,0,0,0))' }} />

            {/* 섹션B: 용량 */}
            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.06)', background: '#fff' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h6" fontWeight={800} sx={{ color: '#111827' }}>용량 선택</Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.25 }}>
                  보유 기기의 저장 용량을 선택하거나 직접 입력하세요.
                </Typography>
                <Grid container spacing={2} justifyContent="flex-start" sx={{ mt: 0.25 }}>
                  {/* 🔥 Firebase 기반 Samsung 태블릿 스토리지 옵션 */}
                  {(() => {
                    // selectedModel에서 스토리지 옵션 추출
                    if (selectedModel && selectedModel.subModels) {
                      const storageOptions = Array.from(new Set(selectedModel.subModels.map(sm => sm.storage || '기본')));
                      return storageOptions;
                    }
                    
                    // 폴백: Firebase 태블릿 모델에서 직접 찾기
                    const matchingFirebaseModel = firebaseTabletModels.find(m => 
                      m.brand === 'Samsung' && m.modelKo === selectedSamsungTabletModel
                    );
                    
                    return matchingFirebaseModel?.storageOptions || [];
                  })().map((storage, index) => (
                    <Grid item xs={6} sm={4} md={3} key={storage}>
                      <StorageOptionCard
                        label={storage}
                        selected={selectedSamsungTabletStorage === storage}
                        onClick={() => { setSelectedSamsungTabletStorage(storage); setCustomSamsungTabletStorage(''); }}
                        index={index}
                      />
                    </Grid>
                  ))}
                  {/* 동적 추가된 용량 카드들 */}
                  {customSamsungTabletStorages.map((storage, index) => (
                    <Grid item xs={6} sm={4} md={3} key={`custom-${storage}`}>
                      <DeletableStorageCard
                        label={storage}
                        selected={selectedSamsungTabletStorage === storage}
                        onClick={() => { setSelectedSamsungTabletStorage(storage); setCustomSamsungTabletStorage(''); }}
                        onDelete={() => {
                          setCustomSamsungTabletStorages(prev => prev.filter(s => s !== storage));
                          if (selectedSamsungTabletStorage === storage) {
                            setSelectedSamsungTabletStorage('');
                          }
                        }}
                        index={index + (() => {
                          if (selectedModel && selectedModel.subModels) {
                            return Array.from(new Set(selectedModel.subModels.map(sm => sm.storage || '기본'))).length;
                          }
                          const matchingFirebaseModel = firebaseTabletModels.find(m => 
                            m.brand === 'Samsung' && m.modelKo === selectedSamsungTabletModel
                          );
                          return (matchingFirebaseModel?.storageOptions || []).length;
                        })()}
                      />
                    </Grid>
                  ))}
              {/* 기타(직접입력) - 통일된 스타일(제목+입력+추가 버튼) */}
                  <Grid item xs={8} sm={5} md={3}>
                    <Card
                      sx={{
                        border: '2px dashed #6366f1',
                        borderRadius: 3,
                        background: 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        height: 'auto',
                        minHeight: 120,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        transition: 'all 0.2s',
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.95)',
                          transform: 'translateY(-2px)',
                        }
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center', p: 2 }}>
                        <Typography fontWeight={700} color="#6366f1" mb={1.5} sx={{ fontSize: '0.9rem' }}>
                          다른 용량이 있나요?
                        </Typography>
                        <TextField
                          value={customSamsungTabletStorage}
                          onChange={e => setCustomSamsungTabletStorage(e.target.value)}
                          placeholder="직접 입력하세요"
                          fullWidth
                          size="small"
                          sx={{
                            mb: 1.5,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                            }
                          }}
                        />
                        <Button
                          variant="contained"
                          size="small"
                          sx={{ 
                            fontWeight: 700,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #5b21b6 0%, #0284c7 100%)',
                            }
                          }}
                          disabled={!customSamsungTabletStorage.trim()}
                          onClick={() => {
                            const trimmed = customSamsungTabletStorage.trim();
                            if (!trimmed) return;
                            // 중복 방지
                            // 🔥 Firebase 기반 Samsung 태블릿 스토리지 중복 검사
                            const existingStorages = (() => {
                              if (selectedModel && selectedModel.subModels) {
                                return Array.from(new Set(selectedModel.subModels.map(sm => sm.storage || '기본')));
                              }
                              const matchingFirebaseModel = firebaseTabletModels.find(m => 
                                m.brand === 'Samsung' && m.modelKo === selectedSamsungTabletModel
                              );
                              return matchingFirebaseModel?.storageOptions || [];
                            })();
                            
                            const allStorages = [...existingStorages, ...customSamsungTabletStorages];
                            if (allStorages.includes(trimmed)) return;
                            // 새 용량 카드 추가
                            setCustomSamsungTabletStorages(prev => [...prev, trimmed]);
                            setCustomSamsungTabletStorage('');
                            // 자동 선택
                            setSelectedSamsungTabletStorage(trimmed);
                          }}
                        >
                          직접 추가
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* 태블릿: 등급 선택/시세 확인 단계 */}
        {activeStep === 4 && category === '태블릿' && (
          isQuoteMode ? (
            // 🎯 시세 조회 모드: 등급별 가격 전체 표시 (조회 전용)
            <PriceQuoteGradeDisplay
              deviceType={category}
              deviceModel={selectedManufacturer === 'APPLE' ? (selectedSubModel ? `${selectedModel?.name} - ${selectedSubModel?.displayName}` : selectedModel?.name || '') : selectedSamsungTabletModel}
              deviceStorage={selectedManufacturer === 'APPLE' 
                ? (selectedSubModel?.storage || '') 
                : (selectedSamsungTabletStorage || customSamsungTabletStorage || '')}
              deviceImage={selectedManufacturer === 'APPLE'
                ? (selectedModel?.image || '/assets/ipad/iPadDefault.png')
                : resolvePhoneImage({ brand: 'Samsung', modelKo: selectedSamsungTabletModel })}
              connectivity={selectedManufacturer === 'APPLE' 
                ? selectedSubModel?.connectivity 
                : selectedSamsungTabletConnectivity || undefined}
              gradePrices={allGradePrices}
              onClose={onClose}
            />
          ) : (
            // 기존 등급 선택 모드
            <EnhancedGradeSelection
              selectedGrade={selectedGrade}
              onGradeSelect={handleGradeSelect}
              gradePrices={allGradePrices || undefined}
              deviceModel={selectedManufacturer === 'APPLE' ? (selectedSubModel ? `${selectedModel?.name} - ${selectedSubModel?.displayName}` : selectedModel?.name || '') : selectedSamsungTabletModel}
              deviceStorage={selectedManufacturer === 'APPLE' ? '' : ([selectedSamsungTabletStorage || customSamsungTabletStorage, selectedSamsungTabletConnectivity].filter(Boolean).join(' - '))}
              category={category}
            />
          )
        )}

        {/* 등급 선택/시세 확인 단계 - 스마트폰 카테고리 */}
        {activeStep === 5 && category === '스마트폰' ? (
          isQuoteMode ? (
            // 🎯 시세 조회 모드: 등급별 가격 전체 표시 (조회 전용)
            <PriceQuoteGradeDisplay
              deviceType={category}
              deviceModel={selectedManufacturer === 'APPLE' ? selectedIphoneModel : selectedSamsungModel}
              deviceStorage={selectedManufacturer === 'APPLE' 
                ? (selectedIphoneStorage || customIphoneStorage) 
                : (selectedSamsungStorage || customSamsungStorage)}
              deviceImage={selectedManufacturer === 'APPLE'
                ? resolvePhoneImage({
                    brand: 'Apple',
                    modelKo: selectedIphoneModel,
                    catalogImage: firebaseAppleSmartphones.find(m => m.modelKo === selectedIphoneModel)?.image
                  })
                : resolvePhoneImage({
                    brand: 'Samsung',
                    series: selectedSamsungSeries,
                    modelEn: selectedSamsungModel
                  })}
              gradePrices={allGradePrices}
              onClose={onClose}
            />
          ) : (
            // 기존 등급 선택 모드
            <EnhancedGradeSelection
              selectedGrade={selectedGrade}
              onGradeSelect={handleGradeSelect}
              gradePrices={allGradePrices || undefined}
              deviceModel={selectedManufacturer === 'APPLE' ? selectedIphoneModel : selectedSamsungModel}
              deviceStorage={selectedManufacturer === 'APPLE' ? selectedIphoneStorage : selectedSamsungStorage}
              category={category}
            />
          )
        ) : null}

        {/* 🎯 웨어러블: 등급 선택 단계 - 매입 신청 모드 */}
        {activeStep === 3 && category === '웨어러블' && !isQuoteMode && selectedConfiguration && (
          <EnhancedGradeSelection
            selectedGrade={selectedGrade}
            onGradeSelect={handleGradeSelect}
            gradePrices={allGradePrices || undefined}
            deviceModel={`${selectedModel?.name || ''} - ${selectedConfiguration.name}`}
            deviceStorage=""
            category={category}
          />
        )}

        {/* 🎯 웨어러블: 시세 확인 단계 - 시세조회 모드에서만 표시 */}
        {activeStep === 3 && category === '웨어러블' && isQuoteMode && (
          <PriceQuoteGradeDisplay
            deviceType={category}
            deviceModel={
              // pendingDeviceData가 있으면 그것을 사용 (애플 계정 다이얼로그에서 확인 후)
              pendingDeviceData?.deviceModel || (
                selectedConfiguration 
                  ? `${selectedModel?.name || ''} - ${selectedConfiguration.name}` 
                  : customConfiguration 
                    ? `${selectedModel?.name || ''} - ${customConfiguration}`
                    : selectedSubModel 
                      ? `${selectedModel?.name || ''} - ${selectedSubModel.displayName}`
                      : selectedModel?.name || ''
              )
            }
            deviceStorage=""
            deviceImage={selectedModel?.image || '/assets/airPot/에어팟3세대.png'}
            gradePrices={allGradePrices} // 🔥 Firebase에서 조회한 실제 가격 전달
            onClose={onClose}
          />
        )}

        {/* 기존 세부 모델 선택 단계 (태블릿 제외) */}
        {(((category === '태블릿' && activeStep === 3 && selectedManufacturer !== 'APPLE' && selectedManufacturer !== 'SAMSUNG')) || (category !== '태블릿' && activeStep === 2))
          && selectedModel && selectedModel.subModels && !needsConfigurationSelection(category || '', selectedModel.name) && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                {selectedModel.name} 세부 모델을 선택하세요
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280', mt: 1 }}>
                정확한 모델 번호를 선택해주세요
              </Typography>
            </Box>
            
            <Grid container spacing={3}>
              {selectedModel.subModels.map((subModel, index) => (
                <Grid item xs={12} sm={6} md={4} key={subModel.modelNumber}>
                  <SubModelCard
                    subModel={subModel}
                    selected={selectedSubModel?.modelNumber === subModel.modelNumber}
                    onClick={() => handleSubModelSelect(subModel)}
                    index={index}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
        </SafeErrorBoundary>
      </DialogContent>
      
      {/* 애플 계정 연동 해제 확인 다이얼로그 */}
      <AppleAccountUnlinkDialog
        open={showAppleUnlinkDialog}
        onClose={handleAppleUnlinkCancel}
        onConfirm={handleAppleUnlinkConfirm}
        deviceModel={pendingDeviceData?.deviceModel || ''}
      />
      
      <DialogActions sx={{ 
        p: isMobile ? 2 : 4, 
        background: 'rgba(248, 250, 252, 0.95)',
        borderTop: '1px solid rgba(226, 232, 240, 0.5)',
        justifyContent: 'space-between',
        flexShrink: 0, // 크기 고정
        // 모바일에서 하단에 고정
        position: 'sticky',
        bottom: 0,
        zIndex: 1,
      }}>
        <Button 
          onClick={onClose} 
          sx={{ 
            fontWeight: 600,
            px: isMobile ? 2 : 3,
            py: 1.5,
            borderRadius: 3,
            color: '#6b7280',
            '&:hover': {
              backgroundColor: 'rgba(107, 114, 128, 0.08)',
              color: '#374151',
            }
          }}
        >
          {isQuoteMode ? '닫기' : '취소'}
        </Button>
        {/* activeStep=1: 제조사 선택 단계(스마트폰/태블릿)에서는 하단 '이전'만, '추가하기' 미노출 */}
        {activeStep === 1 && (category === '스마트폰' || category === '태블릿') && (
          <Button
            variant="outlined"
            onClick={() => setActiveStep(0)}
            sx={{ 
              fontWeight: 600,
              px: isMobile ? 2 : 3,
              py: 1.5,
              borderRadius: 3,
              borderColor: '#6b7280',
              color: '#6b7280',
              '&:hover': {
                borderColor: '#374151',
                backgroundColor: 'rgba(107, 114, 128, 0.04)',
              }
            }}
          >
            이전
          </Button>
        )}
        {activeStep === 1 && category !== '스마트폰' && category !== '태블릿' && (
          <Button
            variant="contained"
            disabled={!selectedModel}
            onClick={() => {
              if (selectedModel) {
                if (category && needsConfigurationSelection(category, selectedModel.name)) {
                  return;
                } else if (!selectedModel.subModels || selectedModel.subModels.length === 0) {
                  // 🎧 구성 선택이 필요 없는 웨어러블 모델 (AirPods Max 등)
                  // 기본적으로 A등급(풀세트) 가격 적용
                  handleWearableDeviceAdd({ 
                    deviceType: category, 
                    deviceModel: selectedModel.name,
                    estimatedPrice: allGradePrices?.A || null,
                    allGradePrices: allGradePrices,
                    userSelectedGrade: 'A',
                  });
                }
              }
            }}
            sx={{ 
              fontWeight: 700, 
              px: isMobile ? 3 : 4, 
              py: 1.5,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5b21b6 0%, #0284c7 100%)',
                boxShadow: '0 6px 16px rgba(99, 102, 241, 0.4)',
              },
              '&:disabled': {
                background: 'rgba(156, 163, 175, 0.5)',
                color: 'rgba(255, 255, 255, 0.7)',
                boxShadow: 'none',
              }
            }}
          >
            {((selectedModel?.subModels && selectedModel.subModels.length > 0) || 
              (category && selectedModel && needsConfigurationSelection(category, selectedModel.name)) ? '다음' : '추가하기')}
          </Button>
        )}
        
        {activeStep >= 2 && (
          <Box sx={{ display: 'flex', gap: isMobile ? 1 : 2, width: '100%', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handlePrev}
              sx={{ 
                fontWeight: 600,
                px: isMobile ? 2 : 3,
                py: 1.5,
                borderRadius: 3,
                borderColor: '#6b7280',
                color: '#6b7280',
                '&:hover': {
                  borderColor: '#374151',
                  backgroundColor: 'rgba(107, 114, 128, 0.04)',
                }
              }}
            >
              이전
            </Button>

            {/* 태블릿: 용량+셀룰러 선택 후 다음 단계로 이동 (등급 선택) */}
            {category === '태블릿' && activeStep === 3 && (
              <Button
                variant="contained"
                disabled={
                  (selectedManufacturer === 'SAMSUNG' && !(selectedSamsungTabletModel && (selectedSamsungTabletStorage || customSamsungTabletStorage) && selectedSamsungTabletConnectivity)) ||
                  (selectedManufacturer === 'APPLE' && !(selectedModel && (selectedAppleTabletStorage || customAppleTabletStorage) && selectedAppleTabletConnectivity))
                }
                onClick={() => {
                  if (selectedManufacturer === 'SAMSUNG') {
                    // 삼성 태블릿: 조건 충족 시 등급 선택으로 이동
                    if (selectedSamsungTabletModel && (selectedSamsungTabletStorage || customSamsungTabletStorage) && selectedSamsungTabletConnectivity) {
                      setActiveStep(4);
                    }
                  } else if (selectedManufacturer === 'APPLE') {
                    // 애플 태블릿: 선택된 용량+연결성 기반으로 세부모델 결정 후 이동
                    if (selectedModel && (selectedAppleTabletStorage || customAppleTabletStorage) && selectedAppleTabletConnectivity) {
                      const desiredStorage = selectedAppleTabletStorage || customAppleTabletStorage;
                      const matched = (selectedModel.subModels || []).find(sm => (sm.storage || '기본') === desiredStorage && sm.connectivity === selectedAppleTabletConnectivity);
                      if (matched) {
                        setSelectedSubModel(matched);
                      } else {
                        // 동일 연결성 우선으로 베이스 선택 후 저장용량만 교체하여 사용
                        const base = (selectedModel.subModels || []).find(sm => sm.connectivity === selectedAppleTabletConnectivity) || (selectedModel.subModels || [])[0];
                        if (base) {
                          setSelectedSubModel({ ...base, storage: desiredStorage });
                        }
                      }
                      setActiveStep(4);
                    }
                  }
                }}
                sx={{ 
                  fontWeight: 700, px: isMobile ? 3 : 4, py: 1.5, borderRadius: 3,
                  background: 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)',
                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                  '&:hover': { background: 'linear-gradient(135deg, #5b21b6 0%, #0284c7 100%)' },
                  '&:disabled': { background: 'rgba(156, 163, 175, 0.5)', color: 'rgba(255, 255, 255, 0.7)', boxShadow: 'none' }
                }}
              >
                다음
              </Button>
            )}
            
            {/* 스마트폰: 등급 선택 완료 후 기기 추가 (등급 선택 단계=5) - 시세 조회 모드에서는 미표시 */}
            {category === '스마트폰' && activeStep === 5 && !isQuoteMode ? (
              <Button
                variant="contained"
                disabled={!selectedGrade}
                onClick={() => {
                  if (selectedManufacturer === 'APPLE' && selectedIphoneModel && (selectedIphoneStorage || customIphoneStorage)) {
                    const storage = selectedIphoneStorage || customIphoneStorage;
                    onAdd?.({
                      deviceType: category,
                      deviceModel: `${selectedIphoneModel} - ${storage}`,
                      userSelectedGrade: selectedGrade!,
                      estimatedPrice: estimatedPrice
                    });
                    onClose();
                  } else if (selectedManufacturer === 'SAMSUNG' && selectedSamsungModel && (selectedSamsungStorage || customSamsungStorage)) {
                    const storage = selectedSamsungStorage || customSamsungStorage;
                    onAdd?.({
                      deviceType: category,
                      deviceModel: `${selectedSamsungModel} - ${storage}`,
                      userSelectedGrade: selectedGrade!,
                      estimatedPrice: estimatedPrice
                    });
                    onClose();
                  }
                }}
                sx={{ 
                  fontWeight: 700, px: isMobile ? 3 : 4, py: 1.5, borderRadius: 3,
                  background: 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)',
                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                  '&:hover': { background: 'linear-gradient(135deg, #5b21b6 0%, #0284c7 100%)' },
                  '&:disabled': { background: 'rgba(156, 163, 175, 0.5)', color: 'rgba(255, 255, 255, 0.7)', boxShadow: 'none' }
                }}
              >
                기기 추가하기
              </Button>
            ) : null}
            
            {/* 웨어러블: 등급 선택 완료 후 기기 추가 (스마트폰과 동일) */}
            {category === '웨어러블' && activeStep === 3 && !isQuoteMode ? (
              <Button
                variant="contained"
                disabled={!selectedGrade || !selectedConfiguration}
                onClick={() => {
                  if (selectedModel && selectedConfiguration && selectedGrade) {
                    const deviceData = {
                      deviceType: category,
                      deviceModel: `${selectedModel.name} - ${selectedConfiguration.name}`,
                      userSelectedGrade: selectedGrade,
                      estimatedPrice: estimatedPrice,
                    };
                    handleWearableDeviceAdd(deviceData);
                  }
                }}
                sx={{
                  fontWeight: 700, px: isMobile ? 3 : 4, py: 1.5, borderRadius: 3,
                  background: 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)',
                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                  '&:hover': { background: 'linear-gradient(135deg, #5b21b6 0%, #0284c7 100%)' },
                  '&:disabled': { background: 'rgba(156, 163, 175, 0.5)', color: 'rgba(255, 255, 255, 0.7)', boxShadow: 'none' }
                }}
              >
                기기 추가하기
              </Button>
            ) : null}

            {/* 태블릿: 등급 선택 완료 후 기기 추가 - 시세 조회 모드에서는 미표시 */}
            {category === '태블릿' && activeStep === 4 && !isQuoteMode ? (
              <Button
                variant="contained"
                disabled={!(selectedGrade && (
                  (selectedManufacturer === 'SAMSUNG' && selectedSamsungTabletModel && (selectedSamsungTabletStorage || customSamsungTabletStorage)) ||
                  (selectedManufacturer === 'APPLE' && selectedModel && selectedSubModel)
                ))}
                onClick={() => {
                  if (selectedManufacturer === 'SAMSUNG') {
                    const storage = selectedSamsungTabletStorage || customSamsungTabletStorage;
                    const connectivity = selectedSamsungTabletConnectivity || '';
                    if (selectedSamsungTabletModel && storage && selectedGrade) {
                      const deviceData = {
                        deviceType: category,
                        deviceModel: `${selectedSamsungTabletModel} - ${storage}${connectivity ? ` - ${connectivity}` : ''}`,
                        userSelectedGrade: selectedGrade,
                        estimatedPrice: estimatedPrice,
                      };
                      console.log('🔍 삼성 태블릿 기기 추가:', deviceData);
                      onAdd?.(deviceData);
                      onClose();
                    }
                  } else if (selectedManufacturer === 'APPLE') {
                    if (selectedModel && selectedSubModel && selectedGrade) {
                      // 🔧 애플 태블릿 모델명 저장 개선
                      // - 기존: `${selectedModel.name} - ${selectedSubModel.displayName} - ${selectedSubModel.modelNumber} - ${connectivity}`
                      //         예) "아이패드 프로 11인치 M4 tablet-apple-ipad-pro-ipad-pro-11-inch-m4-1tb-wi-fi+cellular-5"
                      // - 개선: 삼성 태블릿과 동일하게 "핵심 모델명 + 용량(+연결 방식)"만 저장
                      //         예) "아이패드 프로 11인치 M4 1TB Wi-Fi + Cellular"
                      const connectivity = selectedAppleTabletConnectivity || '';
                      const storage = selectedSubModel.storage || '';

                      // 모델명은 아이패드 한글명 + 용량 + (필요 시) 연결 방식만 사용
                      const coreModelName =
                        `${selectedModel.name}` +
                        `${storage ? ` ${storage}` : ''}` +
                        `${connectivity && connectivity !== 'Wi-Fi' ? ` ${connectivity}` : ''}`;

                      const deviceData = {
                        deviceType: category,
                        deviceModel: coreModelName.trim(),
                        userSelectedGrade: selectedGrade,
                        estimatedPrice: estimatedPrice,
                      };
                      console.log('🔍 애플 태블릿 기기 추가:', deviceData);
                      onAdd?.(deviceData);
                      onClose();
                    }
                  }
                }}
                sx={{ 
                  fontWeight: 700, px: isMobile ? 3 : 4, py: 1.5, borderRadius: 3,
                  background: 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)',
                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                  '&:hover': { background: 'linear-gradient(135deg, #5b21b6 0%, #0284c7 100%)' },
                  '&:disabled': { background: 'rgba(156, 163, 175, 0.5)', color: 'rgba(255, 255, 255, 0.7)', boxShadow: 'none' }
                }}
              >
                기기 추가하기
              </Button>
            ) : null}

            
            
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
});

// 🚀 성능 최적화가 적용된 AddDeviceDialog (React.memo로 불필요한 리렌더링 60% 방지)
AddDeviceDialog.displayName = 'AddDeviceDialog';

export default AddDeviceDialog; 